/**
 * Document Library — the CEO's documents, read here instead of downloaded.
 *
 * Jay, 2026-09-08: "Recipients have to download the file and open it locally, which is awkward, and
 * everyone ends up holding a different version."
 *
 * THE VIEWER IS AN IFRAME, and that is the design rather than a detail. These files are self-contained
 * HTML with their own typography, their own 1500px slide layout and sometimes their own script.
 * Rendering that markup inside this page would put the portal's stylesheet over a layout somebody
 * built to look exactly one way — Jay asked twice for it not to — and would run their script inside
 * the signed-in portal origin. Sandboxed without allow-same-origin; the file is served with a CSP
 * that lets the document be itself and reach nothing else. It is also what makes the mobile
 * requirement work: a 1500px document scrolls and pinch-zooms like a page.
 *
 * THE LIST IS A TABLE, NOT CARDS — Max, 2026-09-13: "cần thiết kế cho nó dễ đọc cho trường hợp có tới
 * hàng trăm tài liệu". Cards are pleasant at six documents and unreadable at two hundred: a row per
 * document with a search box above it is what somebody hunting for HB-001 actually needs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageItem } from './pageContext';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
/* The portal's one sort-and-funnel header, shared with the Sample Tracker, the Project Tracker
   and SAP Ship Doc — Max, 2026-09-21: "cho header nó sort và filter như excel nhé". A second
   implementation is how two tables come to disagree about what a funnel does. */
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import {
  FileText, Upload, Check, History, ArrowLeft, RefreshCw, AlertTriangle, Maximize2, X,
  Search, Users, Share2, Trash2, Link2, Mail, ChevronDown, MessageSquare, Send, Pencil,
  Folder as FolderIcon, Plus, Lock, Columns2, Languages, Download,
} from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import AiTag from './AiTag';
import ListPicker from './ListPicker';
import { Mascot } from './AskBubble';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './DocumentLibrary.css';

const API = `${BACKEND_URL}/api/documents`;

/* A confirmation is worth reading once and then it is clutter — Max, 2026-09-13: "nên hiện tầm 30s
   thui". The timer is keyed on the message itself, so a second message restarts it rather than being
   cut short by the first one's clock. */
const FLASH_MS = 30000;
const useFlash = (initial = '') => {
  const [msg, setMsg] = useState(initial);
  useEffect(() => {
    if (!msg) return undefined;
    const id = setTimeout(() => setMsg(''), FLASH_MS);
    return () => clearTimeout(id);
  }, [msg]);
  return [msg, setMsg];
};

const when = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-GB', {
    timeZone: 'Asia/Ho_Chi_Minh', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/* WHAT THE VIEWER DRAWS depends on what the file is — Max, 2026-09-23. Three cases, and the third
   is the one that had to be decided rather than assumed:
     html, pdf   an iframe. The browser renders both, so the reader never leaves the page and the
                 version they see is the one the portal chose for them.
     png, jpeg   an <img>. A sandboxed iframe around a picture is a frame around a frame.
     doc, ppt,   NO BROWSER RENDERS THESE. The page says so and offers the download, instead of a
     xls         blank frame that looks like something failed to load. Converting them to PDF on
                 the server is the alternative and it is a real piece of infrastructure; it is not
                 this change. */
/* The accept list, written once and matching the server's own table. `accept` is a courtesy — it
   narrows the file picker, it is not a rule — and the server refuses anything else by extension AND
   by first bytes. */
const DOC_ACCEPT = '.html,.htm,.pdf,.png,.jpg,.jpeg,.doc,.docx,.ppt,.pptx,.xls,.xlsx';

/* Codes have four digits since 31/10 (Max: "cho số chạy sẽ là 4 chữ số") and people still type the three they remember:
   "hb-001" finds HB-0001 — a whole code is compared by its number, partial typing as plain text */
const codeNum = (s) => s.replace(/\b([a-z]+)-0*(\d+)\b/g, '$1-$2');
const hasText = (hay, term) => hay.includes(term) || codeNum(hay).includes(codeNum(term));
// a document's kind is its code's prefix — SOP-0003 → SOP (Jay task #1117's Kind filter)
const kindOf = (code) => String(code || '').split('-')[0].toUpperCase();

/* THE LANGUAGES OF ONE VERSION (Hue 02/11: a bilingual document is a Vietnamese file and an English one; Max: one upload
   takes up to the portal's four languages, one file each, and the page reads them — tabs only for the languages there).
   Each language by its own name, the same in every portal language. A file's language is read from its TEXT on the
   server (/lang-guess); a file with no text to read (a picture) falls back to its name (…_VI.html, … EN.pdf), and the
   person corrects either. */
// + Français and 日本語 with the portal's two new languages (task #1173, 04/11)
const DOC_LANGS = ['vi', 'en', 'ko', 'zh', 'fr', 'ja'];
const LANG_NAME = { vi: 'Tiếng Việt', en: 'English', ko: '한국어', zh: '中文', fr: 'Français', ja: '日本語' };
const LANG_SHORT = { vi: 'VI', en: 'EN', ko: 'KO', zh: 'ZH', fr: 'FR', ja: 'JA' };
/* a version's files (Max 03/11): up to eight, several in one language allowed — those are PARTS, read as tabs — and 30 MB
   each, the server's DOC_MAX_BYTES; a bigger file is turned away here, before minutes of upload end in a refusal */
const DOC_MAX_FILES = 8;
const DOC_MAX_MB = 30;
const noExt = (name) => String(name || '').replace(/\.[^.]+$/, '');
// an answer that is not JSON (a proxy's 413 page) must not surface as "Unexpected token '<'"
const answerOf = async (res) => {
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(d.error || (res.status === 413 ? i18n.t('xDoc.serverTooBig', { mb: DOC_MAX_MB }) : i18n.t('xDoc.failed', { status: res.status })));
  return d;
};
const guessLang = (name) => {
  const n = ` ${String(name || '').toLowerCase().replace(/\.[a-z0-9]+$/, '')} `.replace(/[_\-.()[\]]/g, ' ');
  if (/ (vi|vn|vie|viet|vietnamese) |tiếng việt|tieng viet/.test(n)) return 'vi';
  if (/ (en|eng|english) /.test(n)) return 'en';
  if (/ (ko|kr|kor|korean) |한국어/.test(n)) return 'ko';
  if (/ (zh|cn|chn|chinese) |中文/.test(n)) return 'zh';
  if (/ (fr|fra|fre|french|francais) |français/.test(n)) return 'fr';
  if (/ (ja|jp|jpn|japanese) |日本語/.test(n)) return 'ja';
  return '';
};

function Viewer({ docId, versionId, title, fileName, part }) {
  // `part` — one of the version's other-language files (its file id); none = the version's own file
  const { t } = useTranslation();
  const [full, setFull] = useState(false);
  /* AN IMAGE THAT WILL NOT DECODE — Max, 2026-09-23, on a picture that rendered as the browser's
     broken-image icon. The file was a corrupt PNG, and the icon is the browser saying so in a way
     nobody can act on: it does not name the file, does not say what went wrong, and offers nothing.
     The fallback is the panel the Office files already use, so the reader can at least take the
     file away and open it somewhere else. Reset per version, or switching back to a good one keeps
     showing the failure. */
  const [imgFailed, setImgFailed] = useState(false);
  const qs = [versionId ? `version=${versionId}` : '', part ? `part=${part}` : ''].filter(Boolean).join('&');
  const src = `${API}/${docId}/file${qs ? `?${qs}` : ''}`;
  useEffect(() => { setImgFailed(false); }, [src]);
  const ext = String(fileName || '').toLowerCase().match(/\.[a-z0-9]+$/)?.[0] || '.html';
  // an .xlsx is drawn by the server as a page of tables (Jay task #1189, docSheet.js); an old .xls still downloads
  const kind = ['.png', '.jpg', '.jpeg'].includes(ext) ? (imgFailed ? 'download' : 'image')
    : (['.html', '.htm', '.pdf'].includes(ext) ? 'frame' : ext === '.xlsx' ? 'sheet'
      // Word and PowerPoint: the server's PDF of them, in the browser's viewer (Max 03/11, docOffice.js)
      : ['.doc', '.docx', '.ppt', '.pptx'].includes(ext) ? 'office' : 'download');

  if (kind === 'download') {
    return (
      <div className="dl-frame dl-frame-offline">
        <p><b>{fileName}</b></p>
        <p className="dl-hint">{t(imgFailed ? 'doc.imgBroken' : 'doc.noPreview')}</p>
        <a className="sp-btn sp-btn-primary" href={src}>{t('doc.download')}</a>
      </div>
    );
  }
  return (
    <div className={`dl-frame ${full ? 'dl-frame-full' : ''}`}>
      <button type="button" className="dl-full" onClick={() => setFull((v) => !v)}>
        {full ? <X size={14} /> : <Maximize2 size={14} />}
      </button>
      {kind === 'office' ? (
        <iframe title={title || 'document'} src={`${src}${qs ? '&' : '?'}as=pdf`} referrerPolicy="no-referrer" data-dl-office />
      ) : kind === 'sheet' ? (
        /* the workbook as plain tables, no script at all: the strictest sandbox (its sheet links are in-page anchors) */
        <iframe title={title || 'document'} src={`${src}${qs ? '&' : '?'}as=html`} sandbox="" referrerPolicy="no-referrer" data-dl-sheet />
      ) : kind === 'image' ? (
        <img
          className="dl-img"
          src={src}
          alt={title || 'document'}
          onError={() => setImgFailed(true)}
        />
      ) : (
        /* THE SANDBOX IS FOR THE HTML ONES ONLY. On an HTML document it is the whole safety
           argument: allow-scripts so the document's own KO/EN toggle still works, and NO
           allow-same-origin so that script can never reach this portal's session.
           A PDF is not rendered by our page at all — the browser's own viewer draws it, and that
           viewer is an internal page a sandbox without `allow-same-origin` refuses to load. The
           result is a blank frame, which is what this first shipped as. Nothing is given up: the
           file is served `application/pdf` with `nosniff`, so it cannot be treated as a document
           in our origin whatever it contains, and the first bytes were checked before it was
           stored. */
        <iframe
          title={title || 'document'}
          src={src}
          {...(ext === '.pdf' ? {} : { sandbox: 'allow-scripts allow-popups allow-popups-to-escape-sandbox' })}
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );
}

/* Who has read it — Max, 2026-09-13: "user muốn xem những ai đã đọc thì vào đâu". Three states per
   person, in the order they happen: notified, opened, confirmed. Only the people who can send see
   it; for everybody else the question is not theirs to ask. */
function Readers({ docId, onClose }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/${docId}/log`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
        setRows(d.rows || []);
      } catch (e) { setErr(e.message); }
    })();
  }, [docId]);

  const summary = useMemo(() => {
    const list = rows || [];
    return {
      notified: list.length,
      opened: list.filter((r) => r.openedAt).length,
      acked: list.filter((r) => r.acknowledgedAt).length,
      downloaded: list.filter((r) => r.downloads).length,
    };
  }, [rows]);

  return (
    <div className="dl-panel">
      <div className="dl-panel-head">
        <Users size={14} />
        <strong>{t('doc.readers')}</strong>
        {!!rows && <em>{t('doc.readersCount', summary)}</em>}
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
      {!rows && !err && <div className="dl-empty">…</div>}
      {!!rows && !rows.length && <div className="dl-empty">{t('doc.nobody')}</div>}
      {!!rows?.length && (
        <div className="dl-panel-body">
          <table className="dl-table">
            <thead>
              <tr>
                <th>{t('doc.person')}</th><th>{t('doc.version')}</th>
                <th>{t('doc.notified')}</th><th>{t('doc.opened')}</th><th>{t('doc.confirmed')}</th><th>{t('xDoc.downloaded')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.version}-${r.email}`}>
                  <td>{r.name ? `${r.name} · ` : ''}<span className="dl-dim">{r.email}</span></td>
                  <td>{r.version}</td>
                  <td className={r.notifiedAt ? '' : 'dl-dim'}>{when(r.notifiedAt) || '—'}</td>
                  <td className={r.openedAt ? 'dl-yes' : 'dl-dim'}>{when(r.openedAt) || '—'}</td>
                  <td className={r.acknowledgedAt ? 'dl-yes' : 'dl-dim'}>{when(r.acknowledgedAt) || '—'}</td>
                  {/* every download is logged (Jay 02/11): the last one, and how many times */}
                  <td className={r.downloads ? '' : 'dl-dim'} data-dl-downloads={r.email}>{r.downloads ? `${when(r.downloadedAt)}${r.downloads > 1 ? ` (×${r.downloads})` : ''}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* WHO MAY SHARE — Jay, 2026-09-14: "restrict who can share — only the uploader, plus admins that
   uploader delegates. Documents reaching the wrong audience is what I want to avoid."

   The uploader's own list. The whole list is sent on every save, so the screen and the database
   cannot end up disagreeing about who is on it, and the server writes it in one transaction. */
function Sharers({ doc, onClose, onSaved, isOwner }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [people, setPeople] = useState([]);
  const [owner, setOwner] = useState('');
  const [mayDelegate, setMayDelegate] = useState(false);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  /* Several people at once — Max, 2026-09-15. A one-at-a-time <select> meant a save, a reload and a
     scroll back through fifty names for every person on a list that is usually a whole team. */
  const [pick, setPick] = useState([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [find, setFind] = useState('');
  const pickRef = useRef(null);

  useEffect(() => {
    if (!pickOpen) return undefined;
    const away = (e) => { if (pickRef.current && !pickRef.current.contains(e.target)) setPickOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [pickOpen]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [dRes, pRes] = await Promise.all([
        fetch(`${API}/${doc.docId}/delegates`, { credentials: 'include' }),
        fetch(`${API}/people`, { credentials: 'include' }),
      ]);
      const d = await dRes.json();
      if (!dRes.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: dRes.status }));
      setRows(d.delegates || []); setOwner(d.owner || ''); setMayDelegate(!!d.mayDelegate);
      const pd = await pRes.json();
      if (pRes.ok) setPeople(pd.people || []);
      setErr('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [doc.docId]);
  useEffect(() => { load(); }, [load]);

  const write = async (userIds) => {
    setSaving(true); setErr('');
    try {
      const res = await fetch(`${API}/${doc.docId}/delegates`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setRows(d.delegates || []);
      setPick([]); setFind(''); setPickOpen(false);
      onSaved(t('doc.sharersSaved'));
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };

  const ids = rows.map((r) => r.userId);
  /* Nobody who already holds it, and never the uploader — they hold it by being the uploader and a
     row saying so would be a permission that cannot be taken away. */
  const free = people.filter((x) => !ids.includes(x.userId) && x.name !== owner);
  const needle = find.trim().toLowerCase();
  const shown = needle
    ? free.filter((x) => `${x.name} ${x.email}`.toLowerCase().includes(needle))
    : free;

  return (
    <div className="dl-panel">
      <div className="dl-panel-head">
        <Lock size={14} />
        <strong>{t('doc.sharers')}</strong>
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      <div className="dl-panel-body">
        {busy ? <p className="dl-hint-inline">…</p> : (
          <>
            <p className="dl-hint-inline">
              {isOwner
                ? t('doc.sharersIntro', { code: doc.code })
                : t('doc.sharersIntroAdmin', { code: doc.code, owner: owner || '—' })}
            </p>
            <table className="dl-table dl-sharers">
              <tbody>
                <tr className="dl-sharer-owner">
                  <td>{owner || '—'}</td>
                  <td><span className="dl-tag">{t('doc.sharersUploader')}</span></td>
                  <td />
                </tr>
                {rows.map((r) => (
                  <tr key={r.userId}>
                    <td>{r.name}</td>
                    <td>{r.email}</td>
                    <td>
                      {mayDelegate && (
                        <button type="button" className="sp-btn tiny" disabled={saving}
                          onClick={() => write(ids.filter((x) => x !== r.userId))}>
                          <X size={12} /> {t('doc.remove')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && <p className="dl-hint-inline">{t('doc.sharersNone', { owner: owner || '—' })}</p>}
            {mayDelegate && (
              <>
                <div className="dl-field">
                  <span>{t('doc.sharersAdd')}</span>
                  {/* Opened IN FLOW, not floated over the panel. `.dl-panel-body` scrolls at 460px,
                      and an absolutely positioned menu on the last control in it is cut off by that
                      scroll box — the list ended mid-name and the button under it was unreachable
                      without scrolling a menu nobody could see the bottom of. */}
                  <div className="dl-multi dl-multi-inline" ref={pickRef}>
                    <button type="button" className="dl-multi-btn" onClick={() => setPickOpen((v) => !v)}>
                      <span>
                        {pick.length
                          ? free.filter((x) => pick.includes(x.userId)).map((x) => x.name).join(', ')
                          : t('doc.sharersPick')}
                      </span>
                      <ChevronDown size={14} />
                    </button>
                    {pickOpen && (
                      <div className="dl-multi-menu">
                        {/* Fifty names. A box that narrows them is the difference between picking a
                            team and scrolling for one. */}
                        <input className="dl-multi-find" value={find} autoFocus
                          onChange={(e) => setFind(e.target.value)} placeholder={t('doc.sharersFind')} />
                        {shown.map((x) => (
                          <div className="dl-multi-row" key={x.userId}>
                            <label>
                              <input
                                type="checkbox"
                                checked={pick.includes(x.userId)}
                                onChange={() => setPick((prev) => (prev.includes(x.userId)
                                  ? prev.filter((i) => i !== x.userId) : [...prev, x.userId]))}
                              />
                              <span>{x.name}</span>
                              <em>{x.email}</em>
                            </label>
                          </div>
                        ))}
                        {!shown.length && <p className="dl-multi-none">{t('doc.sharersNoMatch')}</p>}
                        {/* INSIDE the menu, stuck to its bottom. Below it the open menu — absolute,
                            z-index 20 — covered the button, so the click that was meant to save
                            landed on a name and ticked it instead. The fifth time this codebase has
                            lost a click to something drawn on top of it. */}
                        <div className="dl-multi-foot">
                          <button type="button" className="sp-btn primary" disabled={saving || !pick.length}
                            onClick={() => write([...ids, ...pick])}>
                            {saving ? t('doc.saving') : t('doc.sharersAddN', { count: pick.length })}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
            {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
          </>
        )}
      </div>
    </div>
  );
}

/* Sharing: a link to copy, or an email the portal sends. Never the file — that is the whole point of
   the feature (Jay: "the email becomes a notification, not a delivery"). */
function Share({ doc, onClose, onSent, isAdmin }) {
  const { t } = useTranslation();
  const [emails, setEmails] = useState('');
  const [note, setNote] = useState('');
  const [groups, setGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  /* The seventh group, typed here — Jay, 2026-09-14. Folded away by default: the common act is
     picking one of the six, and a form open beside a picker invites people to type the address they
     could have chosen. */
  const [adding, setAdding] = useState(false);
  const [gName, setGName] = useState('');
  const [gMail, setGMail] = useState('');
  const [gBusy, setGBusy] = useState(false);
  const [gMsg, setGMsg] = useState('');
  const groupRef = useRef(null);
  const link = `${window.location.origin}/documents/${doc.code}`;

  /* Close the group list when the click lands anywhere else — a menu that stays open over the form
     underneath it is the reason nobody trusts a dropdown. */
  useEffect(() => {
    if (!groupOpen) return undefined;
    const away = (e) => { if (groupRef.current && !groupRef.current.contains(e.target)) setGroupOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [groupOpen]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/groups`, { credentials: 'include' });
        const d = await res.json();
        if (res.ok) setGroups(d.groups || []);
      } catch { /* groups are a convenience; typing an address still works without them */ }
    })();
  }, []);

  /* Both write the WHOLE list back, so the picker cannot disagree with the database about what is
     in it — the add and the remove each return the list the server now holds. */
  const addGroup = async () => {
    setGBusy(true); setErr(''); setGMsg('');
    try {
      const res = await fetch(`${API}/groups`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: gName.trim(), email: gMail.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setGroups(d.groups || []);
      setGMsg(t('doc.groupAdded', { name: gName.trim() }));
      /* Ticked straight away: somebody who stops to add a group is adding it in order to send to it
         now, and making them find it again in the list afterwards is a step with no purpose. */
      const made = (d.groups || []).find((g) => g.email === gMail.trim().toLowerCase());
      if (made) setPicked((prev) => (prev.includes(made.groupId) ? prev : [...prev, made.groupId]));
      setGName(''); setGMail(''); setAdding(false);
    } catch (e) { setErr(e.message); } finally { setGBusy(false); }
  };

  const removeGroup = async (g) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('doc.removeGroupConfirm', { name: g.name }))) return;
    setGBusy(true); setErr(''); setGMsg('');
    try {
      const res = await fetch(`${API}/groups/${g.groupId}`, { method: 'DELETE', credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setGroups(d.groups || []);
      setPicked((prev) => prev.filter((x) => x !== g.groupId));
      setGMsg(t('doc.groupRemoved', { name: g.name }));
    } catch (e) { setErr(e.message); } finally { setGBusy(false); }
  };

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/${doc.docId}/share`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails, groupIds: picked, note }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      onSent(t('doc.sentTo', { count: d.sent }));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="dl-panel">
      <div className="dl-panel-head">
        <Share2 size={14} />
        <strong>{t('doc.share')}</strong>
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      <div className="dl-panel-body">
        <div className="dl-share-link">
          <Link2 size={14} />
          <input readOnly value={link} onFocus={(e) => e.target.select()} />
          <button
            type="button"
            className="sp-btn"
            onClick={async () => {
              /* clipboard.writeText needs a secure context and can be refused; selecting the box is
                 the fallback that always works, and the label says which happened. */
              try { await navigator.clipboard.writeText(link); setCopied(true); } catch { setCopied(false); }
            }}
          >
            {copied ? t('doc.linkCopied') : t('doc.shareLink')}
          </button>
        </div>

        <div className="dl-share-mail">
          <h4><Mail size={13} /> {t('doc.shareByEmail')}</h4>
          <div className="dl-field">
            <span>{t('doc.groups')}</span>
            {/* A dropdown rather than a row of boxes — Max, 2026-09-13. Six groups is already a
                line that wraps; the list will only grow, and a closed control says how many are
                chosen without spending the width. */}
            {/* Inline for the same reason the people picker is: `.dl-panel-body` scrolls at 460px
                and clips anything floated out of it — the sixth group sat below the cut. */}
            <div className="dl-multi dl-multi-inline" ref={groupRef}>
              <button type="button" className="dl-multi-btn" onClick={() => setGroupOpen((v) => !v)}>
                <span>
                  {picked.length
                    ? groups.filter((g) => picked.includes(g.groupId)).map((g) => g.name).join(', ')
                    : t('doc.groupsNone')}
                </span>
                <ChevronDown size={14} />
              </button>
              {groupOpen && (
                <div className="dl-multi-menu">
                  {/* At the TOP, not after the six rows — Max, 2026-09-15: the menu scrolls at 260px
                      and the button sat under the fold, which is the same as not being there. */}
                  {adding ? (
                    <div className="dl-multi-add dl-multi-add-top">
                      <input value={gName} onChange={(e) => setGName(e.target.value)}
                        placeholder={t('doc.addGroupName')} />
                      <input value={gMail} onChange={(e) => setGMail(e.target.value)}
                        placeholder={t('doc.addGroupEmail')} />
                      <p>{t('doc.addGroupHint')}</p>
                      <div>
                        <button type="button" className="sp-btn primary" disabled={gBusy || !gName.trim() || !gMail.trim()}
                          onClick={addGroup}>
                          {gBusy ? t('doc.saving') : t('doc.addGroupSave')}
                        </button>
                        <button type="button" className="sp-btn" onClick={() => setAdding(false)}>
                          {t('doc.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="dl-multi-addbtn dl-multi-addbtn-top"
                      onClick={() => setAdding(true)}>
                      <Plus size={12} /> {t('doc.addGroup')}
                    </button>
                  )}
                  {groups.map((g) => (
                    <div className="dl-multi-row" key={g.groupId}>
                      <label>
                        <input
                          type="checkbox"
                          checked={picked.includes(g.groupId)}
                          onChange={() => setPicked((p) => (p.includes(g.groupId)
                            ? p.filter((x) => x !== g.groupId) : [...p, g.groupId]))}
                        />
                        <span>{g.name}</span>
                        <em>{g.email}</em>
                      </label>
                      {/* Taking a row out changes everybody's picker, not just this one send, which
                          is why it is the administrators' and nobody else's. */}
                      {isAdmin && (
                        <button type="button" className="dl-multi-x" disabled={gBusy}
                          title={t('doc.remove')} onClick={() => removeGroup(g)}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {!!picked.length && (
                    <button type="button" className="dl-multi-clear" onClick={() => setPicked([])}>
                      {t('doc.groupsClear')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {!!gMsg && <p className="dl-hint-inline dl-hint-ok">{gMsg}</p>}
          <label className="dl-field">
            <span>{t('doc.emails')}</span>
            <input
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="norbert@…, chiwang@…"
            />
          </label>
          <p className="dl-hint-inline">{t('doc.emailsHint')}</p>
          <label className="dl-field dl-field-top">
            <span>{t('doc.sendNote')}</span>
            {/* Three lines — Max, 2026-09-13. One line was enough for "Q3 table corrected" and not
                for the sentence somebody actually wants to write when a handbook changes. */}
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
          <div className="dl-panel-foot">
            <button type="button" className="sp-btn primary" onClick={send} disabled={busy || (!emails.trim() && !picked.length)}>
              {busy ? t('doc.sending') : t('doc.send')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Removing a document is the one action on this screen that cannot be undone from the screen, so it
   asks in the page rather than through window.confirm — a browser dialog carries no document name a
   person can check, and Chrome hides it entirely on a second press. Its own overlay z-index: every
   .st-modal-overlay in this codebase shares one layer, and a dialog on that layer has had its buttons
   silently swallowed three times. */
function Confirm({ title, subject, body, danger, confirmLabel, cancelLabel, onConfirm, onCancel }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.focus();
    const key = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onCancel]);
  return (
    <div className="dl-confirm-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dl-confirm" role="dialog" aria-modal="true">
        <h4>{title}</h4>
        {!!subject && <p className="dl-confirm-subject">{subject}</p>}
        <p>{body}</p>
        <div className="dl-confirm-foot">
          <button type="button" className="sp-btn" onClick={onCancel}>{cancelLabel}</button>
          <button type="button" ref={ref} className={`sp-btn ${danger ? 'dl-danger-solid' : 'primary'}`}
            onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* WHO CATCHY SHARES A DOCUMENT WITH (Jay task #1186): all staff, the people who may open it here, or chosen departments and roles —
   Admins always. Opened to change it (folder owners and Admins), and when a document is switched on, preset to its folder's default
   (Costing: the Costing department and the Costing and CEO roles). On the body, above every page layer, like Confirm. */
const audWho = (aud, opts, t) => {
  if (!aud) return '';
  if (aud.mode === 'all') return t('xDoc.audAll');
  if (aud.mode !== 'limited') return t('xDoc.audReaders');
  const nm = (list, k) => (list || []).find((x) => x.key === k)?.name || k;
  // departments and roles apart — "Costing" is both a department and a role
  return [(aud.teams || []).length ? `${t('xDoc.audDepts')}: ${aud.teams.map((k) => nm(opts?.teams, k)).join(', ')}` : '',
    (aud.roles || []).length ? `${t('xDoc.audRoles')}: ${aud.roles.map((k) => nm(opts?.roles, k)).join(', ')}` : '',
    t('xDoc.audAdmins')].filter(Boolean).join(' · ');
};
function AudienceDialog({ docId, activate, subject, onSaved, onActivate, onCancel }) {
  const { t } = useTranslation();
  const [opts, setOpts] = useState(null);
  const [aud, setAud] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    fetch(`${API}/${docId}/catchy-audience`, { credentials: 'include' }).then(async (r) => {
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setOpts(d);
      // switching on with nothing chosen yet: the folder's default; otherwise what is set
      const a = activate && !d.audience.explicit ? d.default : d.audience;
      setAud({ mode: a.mode, roles: a.roles || [], teams: a.teams || [] });
    }).catch((e) => setErr(e.message));
  }, [docId, activate]);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onCancel]);
  const toggle = (kind, k) => setAud((a) => ({ ...a, [kind]: a[kind].includes(k) ? a[kind].filter((x) => x !== k) : [...a[kind], k] }));
  const empty = aud?.mode === 'limited' && !aud.roles.length && !aud.teams.length;
  const save = async () => {
    if (empty) { setErr(t('xDoc.audPickOne')); return; }
    if (activate) { onActivate(aud); return; }
    setBusy(true); setErr('');
    try {
      const r = await fetch(`${API}/${docId}/catchy-audience`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(aud) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      onSaved(audWho(d.audience, opts, t));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return createPortal(
    <div className="dl-confirm-back dl-aud-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dl-confirm dl-aud" role="dialog" aria-modal="true" data-dl-aud>
        <h4>{activate ? t('xDoc.catchyWarnTitle') : t('xDoc.audTitle')}</h4>
        {!!subject && <p className="dl-confirm-subject">{subject}</p>}
        <p>{activate ? t('xDoc.catchyWarnAud') : t('xDoc.audHint')}</p>
        {!aud && !err && <p className="dl-dim">…</p>}
        {aud && (
          <div className="dl-aud-modes">
            {['all', 'readers', 'limited'].map((m) => (
              <label key={m} className={aud.mode === m ? 'on' : ''}>
                <input type="radio" name="dl-aud" checked={aud.mode === m} onChange={() => setAud((a) => ({ ...a, mode: m }))} data-dl-audmode={m} />
                {t(m === 'all' ? 'xDoc.audAll' : m === 'readers' ? 'xDoc.audReaders' : 'xDoc.audLimited')}
              </label>
            ))}
          </div>
        )}
        {aud?.mode === 'limited' && opts && (
          <div className="dl-aud-pick">
            <div><b>{t('xDoc.audDepts')}</b>
              <div className="dl-aud-chips">{opts.teams.map((x) => (
                <label key={x.key} className={aud.teams.includes(x.key) ? 'on' : ''}>
                  <input type="checkbox" checked={aud.teams.includes(x.key)} onChange={() => toggle('teams', x.key)} data-dl-audteam={x.key} />{x.name}
                </label>
              ))}</div>
            </div>
            <div><b>{t('xDoc.audRoles')}</b>
              <div className="dl-aud-chips">{opts.roles.map((x) => (
                <label key={x.key} className={aud.roles.includes(x.key) ? 'on' : ''}>
                  <input type="checkbox" checked={aud.roles.includes(x.key)} onChange={() => toggle('roles', x.key)} data-dl-audrole={x.key} />{x.name}
                </label>
              ))}</div>
            </div>
            <p className="dl-dim">{t('xDoc.audAdmins')}</p>
          </div>
        )}
        {opts && <p className="dl-dim dl-aud-default">{opts.default.why === 'costing' ? t('xDoc.audDefaultCosting') : t('xDoc.audDefaultRule')}</p>}
        {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
        <div className="dl-confirm-foot">
          <button type="button" className="sp-btn" onClick={onCancel}>{t('doc.cancel')}</button>
          <button type="button" className="sp-btn primary" disabled={busy || !aud || empty} onClick={save} data-dl-audsave>
            {activate ? t('xDoc.catchyActivate') : t('xDoc.audSave')}
          </button>
        </div>
      </div>
    </div>, document.body);
}

/* SEVERAL DOCUMENTS ON OR OFF FOR CATCHY (Jay task #1120). The server is asked first what WOULD happen (dry), and that is
   what this lists: the documents that will be switched, and the ones skipped with why — a folder that is not yours, a
   document with no version. Switching on shows the same warning as one by one. Its own overlay layer, like Confirm. */
const SKIP_KEY = { 'not your folder': 'skip_folder', 'no version uploaded': 'skip_noversion', 'already on': 'skip_on', 'already off': 'skip_off', 'not found': 'skip_missing' };
const skipWhy = (t, r) => (SKIP_KEY[r] ? t(`xDoc.${SKIP_KEY[r]}`) : r);
function BulkCatchy({ plan, busy, onConfirm, onCancel }) {
  const { t } = useTranslation();
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [onCancel]);
  const n = plan.done.length;
  return (
    <div className="dl-confirm-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="dl-confirm dl-bulk" role="dialog" aria-modal="true" data-dl-bulk={plan.on ? 'on' : 'off'}>
        <h4>{t(plan.on ? 'xDoc.bulkOnTitle' : 'xDoc.bulkOffTitle', { n })}</h4>
        <p>{plan.on ? t('xDoc.catchyWarn') : t('xDoc.catchyOffBody')}</p>
        {n ? (
          <>
            <div className="dl-bulk-h">{t('xDoc.bulkWill')} · {n}</div>
            <ul className="dl-bulk-list" data-dl-bulk-will>{plan.done.map((d) => <li key={d.docId}><b>{d.code}</b> {d.title}{d.label ? <em> · {d.label}</em> : null}</li>)}</ul>
          </>
        ) : <p className="dl-bulk-none">{t('xDoc.bulkNone')}</p>}
        {!!plan.skipped.length && (
          <>
            <div className="dl-bulk-h skip">{t('xDoc.bulkSkip')} · {plan.skipped.length}</div>
            <ul className="dl-bulk-list skip" data-dl-bulk-skip>{plan.skipped.map((d) => <li key={d.docId}><b>{d.code}</b> {d.title} <em>— {skipWhy(t, d.reason)}</em></li>)}</ul>
          </>
        )}
        <div className="dl-confirm-foot">
          <button type="button" className="sp-btn" onClick={onCancel}>{t('doc.cancel')}</button>
          <button type="button" className={`sp-btn ${plan.on ? 'primary' : 'dl-danger-solid'}`} disabled={!n || busy} onClick={onConfirm} data-dl-bulk-go>
            {plan.on ? t('xDoc.bulkOn') : t('xDoc.bulkOff')}{n ? ` (${n})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Comments — Max, 2026-09-13: "tôi muốn có phần comment á".

   Under the document and above the version history, because that is the order the questions arrive
   in: read it, ask about it, then wonder what changed last time. One flat list, oldest first, the way
   a conversation is read — not newest-first, which is right for a feed and wrong for a discussion of
   four remarks. Each comment carries the version that was current when it was written, so an old
   question about a table that has since been rewritten still explains itself. */
function Comments({ docId, canEdit }) {
  const { t } = useTranslation();
  const [state, setState] = useState(null);   // { comments, me, canModerate }
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/${docId}/comments`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setState(d);
    } catch (e) { setErr(e.message); }
  }, [docId]);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/${docId}/comments`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setState(d); setDraft('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const remove = async (commentId) => {
    setRemoving(null);
    try {
      const res = await fetch(`${API}/${docId}/comments/${commentId}`, {
        method: 'DELETE', credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setState(d);
    } catch (e) { setErr(e.message); }
  };

  const rows = state?.comments || [];
  const live = rows.filter((c) => !c.deleted);

  return (
    <div className="dl-comments">
      <h3>
        <MessageSquare size={14} /> {t('doc.comments')}
        {!!live.length && <em>{live.length}</em>}
      </h3>
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
      {!rows.length && <p className="dl-comment-none">{t('doc.noComments')}</p>}
      {rows.map((c) => (
        <div key={c.commentId} className={`dl-comment ${c.deleted ? 'dl-comment-gone' : ''}`}>
          <div className="dl-comment-head">
            <strong>{c.author}</strong>
            <span>{when(c.createdAt)}</span>
            {!!c.version && <i>{c.version}</i>}
            {!c.deleted && (state?.canModerate || c.userId === state?.me) && (
              <button type="button" className="dl-comment-x" title={t('doc.deleteComment')}
                onClick={() => setRemoving(c)}>
                <Trash2 size={12} />
              </button>
            )}
          </div>
          {/* The body is plain text held in a div, never markup: it is typed by a person and read by
              everybody else who can open the document. white-space: pre-wrap keeps their line breaks. */}
          <div className="dl-comment-body">{c.deleted ? t('doc.commentGone') : c.body}</div>
        </div>
      ))}

      <div className="dl-comment-new">
        <textarea
          rows={3}
          value={draft}
          maxLength={4000}
          placeholder={t('doc.commentPlaceholder')}
          onChange={(e) => setDraft(e.target.value)}
          /* Ctrl/⌘+Enter posts. Enter alone must stay a line break — a comment about a document is
             usually more than one sentence. */
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) post(); }}
        />
        <div className="dl-comment-foot">
          <span className="dl-hint-inline">{t('doc.commentHint')}</span>
          <button type="button" className="sp-btn primary" onClick={post} disabled={busy || !draft.trim()}>
            <Send size={13} /> {busy ? t('doc.posting') : t('doc.post')}
          </button>
        </div>
      </div>

      {!!removing && (
        <Confirm
          title={t('doc.deleteCommentTitle')}
          subject={`${removing.author} · ${when(removing.createdAt)}`}
          body={t('doc.deleteCommentBody')}
          danger
          confirmLabel={t('doc.deleteComment')}
          cancelLabel={t('doc.cancel')}
          onConfirm={() => remove(removing.commentId)}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
}

/* What a document says about itself, after it exists — Max, 2026-09-13: "những document đã tạo thì
   ko thấy chỗ để edit á". Everything here was a one-shot decision on the upload form, and the tick
   that matters most was among them: a document uploaded without "everyone may read it" was invisible
   to the people it was written for and there was no way to widen it.

   The CODE and the KIND are not here on purpose. The code is the permanent address every mail points
   at; the kind is what that code was issued from. */
/* One folder picker for both the upload panel and the edit panel. The options are the tree
   flattened with an indent, because a <select> cannot nest and "Costing" on its own is ambiguous the
   moment a second Costing appears under another parent. */
/* A REQUIRED FIELD SAYS SO (Max 03/11: "cái nào bắt buộc thì nên có dấu đỏ") — a red star after its label */
const Req = () => <b className="dl-req" aria-hidden="true">*</b>;

function FolderPicker({ value, onChange, label, required = false }) {
  const { t } = useTranslation();
  const [folders, setFolders] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/folders`, { credentials: 'include' });
        if (res.ok) setFolders((await res.json()).folders || []);
      } catch { /* the field simply offers Unfiled; the document can be filed later */ }
    })();
  }, []);
  const flat = [];
  const walk = (parentId, depth) => {
    folders.filter((f) => (f.parentId || null) === (parentId || null)).forEach((f) => {
      flat.push({ ...f, depth });
      walk(f.folderId, depth + 1);
    });
  };
  walk(null, 0);
  return (
    <label className="dl-field">
      <span>{label || t('doc.folderField')}{required && <Req />}</span>
      {/* on a new document the folder starts empty and must be chosen (Max 03/11: "mục folder cũng vậy nên để trống để user
          tự chọn"); Edit still offers Unfiled */}
      <select value={value || ''} onChange={(e) => onChange(Number(e.target.value) || null)} className={required && !value ? 'dl-unset' : ''} data-dl-folderpick={required ? 'new' : 'edit'}>
        {required ? <option value="" disabled>{t('xDoc.folderChoose')}</option> : <option value="">{t('doc.folderNone')}</option>}
        {flat.map((f) => (
          <option key={f.folderId} value={f.folderId}>
            {`${'\u00a0\u00a0'.repeat(f.depth)}${f.depth ? '└ ' : ''}${f.name}`}
          </option>
        ))}
      </select>
    </label>
  );
}

/* THE AUTHOR, FOUND BY TYPING (Max 31/10: "chỗ author thêm cái search tên để tìm và chọn"): ~90 accounts in one <select>
   meant scrolling past Ilaria, Jade, Jae-eun… to reach Kayla. The portal's own search-and-pick box (ListPicker, closed list —
   nobody is added from here), over the same labels the select showed; the value stays the account id. Empty = `emptyLabel`. */
function AuthorPicker({ people, value, onChange, emptyLabel, testid }) {
  const labelOf = (p) => p.label || p.name;
  const cur = people.find((p) => String(p.userId) === String(value));
  return (
    <div className="dl-author-picker" data-dl-author-picker={testid}>
      <ListPicker
        value={cur ? labelOf(cur) : ''}
        options={people.map(labelOf)}
        allowAdd={false}
        placeholder={emptyLabel}
        onChange={(label) => {
          const p = people.find((x) => labelOf(x) === label);
          onChange(p ? String(p.userId) : '');
        }}
      />
    </div>
  );
}

function EditDoc({ doc, onSaved, onClose, canShare, canFixVersion, onKindChanged }) {
  const { t } = useTranslation();
  const current = doc.versions.find((v) => v.versionId === doc.currentVersionId);
  const [title, setTitle] = useState(doc.title || '');
  const [summary, setSummary] = useState(doc.summary || '');
  const [everyone, setEveryone] = useState(doc.visibility === 'everyone');
  const [note, setNote] = useState(current?.changeNote || '');
  // the current version's number, corrected by the folder's owners and Admins (Jay task #1180: MAN-0012 is v2.1, not v1.0)
  const [verLabel, setVerLabel] = useState(current?.label || '');
  const [folderId, setFolderId] = useState(doc.folderId || null);
  /* Editable here as well as at upload, for the reason the version box is: an author picked wrongly
     on the way in should not need a code change to correct. */
  const [authorId, setAuthorId] = useState(doc.authorUserId ? String(doc.authorUserId) : '');
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  /* THE KIND (Max 03/11): changing it gives the document the next code of its new kind, keeps the old code working and
     mails everybody it was shared with — so it is offered to whoever may send the document, and to its author (the server
     asks again) */
  const curKind = String(doc.code || '').split('-')[0];
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState(curKind);
  const [kindBusy, setKindBusy] = useState(false);
  useEffect(() => {
    if (!canShare) return;
    fetch(`${API}/meta/kinds`, { credentials: 'include' }).then((r) => r.json()).then((d) => setKinds(d.kinds || [])).catch(() => {});
  }, [canShare]);
  const nextOf = kinds.find((k) => k.key === kind)?.nextCode || '…';
  /* THE REST OF THE FORM FIRST (Jay task #1189: "after a kind change, the new code is created but the folder does not always update")
     — "Change kind" used to change the code and close the panel, dropping a folder (or title, author…) picked in the same visit.
     Now either button saves the fields, then changes the kind. */
  const changeKind = async () => {
    setKindBusy(true); setErr('');
    try {
      await putFields();
      const d = await answerOf(await fetch(`${API}/${doc.docId}/kind`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }),
      }));
      onKindChanged(d.code, d.oldCode, d.notified);
    } catch (e) { setErr(e.message); } finally { setKindBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/people`, { credentials: 'include' });
        const d = await res.json();
        if (alive && res.ok) setPeople(d.people || []);
      } catch (e) { /* the picker stays empty; an author is optional */ }
    })();
    return () => { alive = false; };
  }, []);

  const putFields = async () => {
      const res = await fetch(`${API}/${doc.docId}`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title, summary, visibility: everyone ? 'everyone' : 'audience',
          // Always sent, including as null: the server treats an absent folderId as "leave it where
          // it is", which is what an edit of the title alone must not change.
          folderId,
          // Always sent, including as null, for the same reason as folderId: the server reads an
          // ABSENT key as "leave it alone", so clearing the author has to arrive as an explicit null.
          authorUserId: Number(authorId) || null,
          // only sent when there is a version to carry it
          ...(current ? { changeNote: note } : {}),
          ...(canFixVersion && current && verLabel.trim() && verLabel.trim() !== current.label ? { versionLabel: verLabel.trim() } : {}),
        }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
  };
  const save = async () => {
    // a kind picked and Save pressed: the same as "Change kind" — fields, then the kind
    if (canShare && kind !== curKind && kinds.length) { changeKind(); return; }
    setBusy(true); setErr('');
    try {
      await putFields();
      onSaved(t('doc.savedMsg'));
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="dl-panel">
      <div className="dl-panel-head">
        <Pencil size={14} />
        <strong>{t('doc.edit')}</strong>
        <em>{doc.code}</em>
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      <div className="dl-panel-body">
        {canShare && (
          <div className="dl-field dl-kindrow" data-dl-kind>
            <span>{t('doc.kind')}</span>
            <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label={t('doc.kind')} data-dl-kindsel>
              {(kinds.length ? kinds : [{ key: curKind, label: curKind }]).map((k) => <option key={k.key} value={k.key}>{k.label} ({k.key})</option>)}
            </select>
            {kind !== curKind && (
              <>
                <button type="button" className="sp-btn primary" disabled={kindBusy || !kinds.length} onClick={changeKind} data-dl-kindgo>
                  {kindBusy ? t('doc.saving') : t('xDoc.kindChange', { code: nextOf })}
                </button>
                <span className="dl-hint" data-dl-kindhint>{t('xDoc.kindHint', { old: doc.code, code: nextOf })}</span>
              </>
            )}
          </div>
        )}
        <label className="dl-field">
          <span>{t('doc.docTitle')}<Req /></span>
          <input value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="dl-field">
          <span>{t('doc.summary')}</span>
          <input value={summary} maxLength={1000} onChange={(e) => setSummary(e.target.value)} />
        </label>
        <div className="dl-field">
          <span>{t('doc.author')}</span>
          <AuthorPicker people={people} value={authorId} onChange={setAuthorId} emptyLabel={t('doc.authorNone')} testid="edit" />
          <span className="dl-hint">{t('doc.authorHint')}</span>
        </div>
        <FolderPicker value={folderId} onChange={setFolderId} />
        <label className="dl-check">
          <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />
          <span>{t('doc.everyone')}</span>
        </label>
        {!everyone && <p className="dl-hint">{t('doc.narrowHint')}</p>}
        {canFixVersion && !!current && (
          <label className="dl-field" data-dl-fixver>
            <span>{t('xDoc.fixVersion')}</span>
            <input value={verLabel} onChange={(e) => setVerLabel(e.target.value)} placeholder="v1.0" style={{ maxWidth: 120 }} data-dl-fixverinput />
            <span className="dl-hint-plain">{t('xDoc.fixVersionHint')}</span>
          </label>
        )}
        {!!current && (
          <label className="dl-field">
            <span>{t('doc.changeNoteLabel')}{current.changeNoteAi && note === current.changeNote && (
              <> <AiTag size={13} title={t('xDoc.aiNoteTitle')} /> <small className="dl-ai-note">{t('xDoc.aiNoteEdit')}</small></>)}</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              placeholder={current.label} />
          </label>
        )}
        {/* Grey, not the amber of .dl-hint: this says which version the note belongs to, it does not
            warn about anything. */}
        {!!current && <p className="dl-hint-plain">{t('doc.noteOfVersion', { version: current.label })}</p>}
        {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
        <div className="dl-panel-foot">
          <button type="button" className="sp-btn primary" onClick={save} disabled={busy || !title.trim()}>
            {busy ? t('doc.saving') : t('doc.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentPage({ code, onRemoved }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  /* Sharing stopped being the edit level on 2026-09-15 (Jay). The server decides; the screen only
     draws what the server said, because a button that is drawn and then refused is worse than no
     button at all. */
  const [share, setShare] = useState({ canShare: false, isOwner: false, isAdmin: false, uploadedBy: '', canKind: false });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useFlash('');
  const [busy, setBusy] = useState(true);
  const [showing, setShowing] = useState(null);
  /* which language is read: { one: lang } or side by side { both: true, left, right }. Starts on the file in the portal's
     language; side by side is remembered for the next document in several languages, the single language is not (it
     follows the portal's) */
  const [langView, setLangView] = useState(null);
  const [panel, setPanel] = useState('');
  const [acking, setAcking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /* CATCHY (task #105): what Catchy reads of this document, and whether this person may switch it (folder owner / Admin) */
  const [catchy, setCatchy] = useState({ on: null, may: false });
  const [catchyAsk, setCatchyAsk] = useState('');   // 'on' | 'off' while the warning is open
  const [catchyBusy, setCatchyBusy] = useState(false);
  const [audNames, setAudNames] = useState(null);   // role / department names, for the "shares it with" line (task #1186)

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/${encodeURIComponent(code)}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setData({ ...d.document, formerCodes: d.formerCodes || [] }); setCanEdit(!!d.canEdit);
      setShare({
        canShare: !!d.canShare, isOwner: !!d.isOwner, isAdmin: !!d.isAdmin, uploadedBy: d.uploadedBy || '',
        canKind: !!d.canKind, canFixVersion: !!d.canFixVersion,
      });
      setCatchy({ on: d.catchy || null, may: !!d.mayCatchy });
      if (d.catchy?.audience?.mode === 'limited') {
        fetch(`${API}/${d.document.docId}/catchy-audience`, { credentials: 'include' }).then((r) => r.json()).then(setAudNames).catch(() => {});
      }
      setErr('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [code]);
  useEffect(() => { load(); }, [load]);
  // opened by a code it no longer has (its kind changed, 03/11): the address follows to the code it has now
  useEffect(() => {
    if (data?.code && (data.formerCodes || []).length && String(code).toUpperCase() !== String(data.code).toUpperCase()) {
      navigate(`/documents/${encodeURIComponent(data.code)}`, { replace: true });
    }
  }, [data, code, navigate]);
  // the floating Catchy knows which document and version are open (Jay 30/10, pageContext.js)
  const readingNow = data ? (showing || data.versions.find((v) => v.versionId === data.currentVersionId)) : null;
  usePageItem(data ? { kind: 'document', id: data.code, version: readingNow?.label || '', label: data.title, screen: 'Document Library' } : null);

  const acknowledge = async () => {
    setAcking(true);
    try {
      const res = await fetch(`${API}/${data.docId}/acknowledge`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      await load();
    } catch (e) { setErr(e.message); } finally { setAcking(false); }
  };

  const switchCatchy = async (on, audience = null) => {
    setCatchyAsk(''); setCatchyBusy(true);
    try {
      const res = await fetch(`${API}/${data.docId}/catchy`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ on, ...(audience ? { audience } : {}) }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setErr('');
      setMsg(on ? t('xDoc.catchyOnMsg', { version: d.label, n: d.passages }) : t('xDoc.catchyOffMsg'));
      await load();
    } catch (e) { setErr(e.message); } finally { setCatchyBusy(false); }
  };

  const remove = async () => {
    setConfirming(false);
    try {
      const res = await fetch(`${API}/${data.docId}`, { method: 'DELETE', credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      /* Tell the list it is out of date BEFORE leaving. Without this the removed row was still on
         the screen you land on: the list loads once when the page mounts, and going back to it is a
         route change, not a mount (Max, 2026-09-13: "xóa rồi vẫn hiện"). */
      onRemoved(t('doc.removedMsg', { code: data.code }));
      navigate('/documents');
    } catch (e) { setErr(e.message); }
  };

  if (busy && !data) return <div className="dl-empty">…</div>;
  if (err && !data) return <div className="sp-error"><AlertTriangle size={14} /> {err}</div>;
  if (!data) return null;

  const current = data.versions.find((v) => v.versionId === data.currentVersionId);
  const reading = showing || current;
  /* the languages of the version being read and which one is on screen — the reader below draws them, and Download takes
     the one being read (in side by side, the left column) */
  /* every file of the version, each with a key: the version's own file ('main', part 1 of its language), then the others —
     other languages, or further PARTS of a language (Max 03/11). The reader picks a language, then a part. */
  const langFiles = reading ? [{ key: 'main', part: null, partNo: 1, lang: reading.lang || null, fileName: reading.fileName || '' },
    ...(reading.others || []).map((o) => ({ ...o, key: `p${o.part}` }))] : [];
  const partsOfLang = (l) => langFiles.filter((f) => f.lang === l);
  const fileLabel = (f, i) => `${LANG_NAME[f.lang] || t('xDoc.fileN', { n: i + 1 })}${partsOfLang(f.lang).length > 1 ? ` · ${noExt(f.fileName) || t('xDoc.partN', { n: f.partNo || 1 })}` : ''}`;
  let rememberBoth = false;
  try { rememberBoth = localStorage.getItem('dlLangView') === 'both'; } catch (e) { /* private window */ }
  const portalLang = String(i18n.language || 'en').slice(0, 2);
  const startKey = (langFiles.find((f) => f.lang === portalLang) || langFiles[0])?.key;
  const langV = langView || (rememberBoth && langFiles.length > 1 ? { both: true, left: langFiles[0].key, right: langFiles[1].key } : { one: startKey });
  const fileOfKey = (k) => langFiles.find((f) => f.key === k) || langFiles[0];
  const dlFile = langFiles.length > 1 ? fileOfKey(langV.both ? langV.left : langV.one) : null;
  const dlHref = data.currentVersionId
    ? `${API}/${data.docId}/download?version=${reading?.versionId || data.currentVersionId}${dlFile?.part ? `&part=${dlFile.part}` : ''}` : null;

  return (
    <>
      <div className="dl-head">
        <button type="button" className="sp-btn" onClick={() => navigate('/documents')}>
          <ArrowLeft size={13} /> {t('doc.allDocuments')}
        </button>
        <span className="dl-code">{data.code}</span>
        {(data.formerCodes || []).length > 0 && <span className="dl-formerly" data-dl-formerly>{t('xDoc.formerly', { codes: data.formerCodes.join(', ') })}</span>}
        <h2>{data.title}</h2>
        {!!reading && <span className="dl-ver">{reading.label}</span>}
        {!!reading?.author && <span className="dl-ver-author" data-dl-author>{t('doc.byAuthor', { name: reading.author })}</span>}
        {showing && showing.versionId !== data.currentVersionId && (
          <span className="dl-oldver">
            {t('doc.readingOld')}{' '}
            <button type="button" onClick={() => setShowing(null)}>{t('doc.backToLatest')}</button>
          </span>
        )}
        {/* DOWNLOAD, for everyone who can read it (the link is in Share — Max 02/11: no Copy link button beside it) (Jay 02/11: Sanghyun could not download DOC-0001 v2.0 —
            an HTML document lives in a frame); the copy carries its version and "Check HEVN Portal for the latest version" */}
        {/* one row of buttons that wraps as a whole (Max 02/11: "các nút đều nằm trên 1 hàng") — a long title pushes the
            row under it, never splits Download from the rest */}
        {!!dlHref && !canEdit && (
          <div className="dl-head-actions dl-head-read">
            <a className="sp-btn" href={dlHref} download data-dl-download title={t('xDoc.downloadTitle')}><Download size={13} /> {t('xDoc.download')}</a>
          </div>
        )}
        {canEdit && (
          <div className="dl-head-actions">
            {!!dlHref && <a className="sp-btn" href={dlHref} download data-dl-download title={t('xDoc.downloadTitle')}><Download size={13} /> {t('xDoc.download')}</a>}
            {/* FIRST IN THE ROW — Max, 2026-09-22: "nút new version nên để ở trên luôn". It was on
                the Version history heading, which is where the versions ARE but not where somebody
                looks for what they can do to this document: every other action on it is up here,
                and one of them living somewhere else is the same hunt Jay complained about, moved
                a few hundred pixels. */}
            <button type="button" className={`sp-btn ${panel === 'version' ? 'cap-on' : ''}`}
              onClick={() => setPanel(panel === 'version' ? '' : 'version')}>
              <Upload size={13} /> {t('doc.newVersion')}
            </button>
            <button type="button" className={`sp-btn ${panel === 'edit' ? 'cap-on' : ''}`}
              onClick={() => setPanel(panel === 'edit' ? '' : 'edit')}>
              <Pencil size={13} /> {t('doc.edit')}
            </button>
            <button type="button" className={`sp-btn ${panel === 'readers' ? 'cap-on' : ''}`}
              onClick={() => setPanel(panel === 'readers' ? '' : 'readers')}>
              <Users size={13} /> {t('doc.readers')}
            </button>
            {share.canShare ? (
              <button type="button" className={`sp-btn ${panel === 'share' ? 'cap-on' : ''}`}
                onClick={() => setPanel(panel === 'share' ? '' : 'share')}>
                <Share2 size={13} /> {t('doc.share')}
              </button>
            ) : (
              /* Said rather than hidden. Somebody who can see Edit and Readers but no Share will
                 otherwise read it as the portal having lost a button. */
              <span className="dl-cantshare" title={t('doc.cannotShare', { owner: share.uploadedBy || '—' })}>
                <Lock size={12} /> {t('doc.cannotShare', { owner: share.uploadedBy || '—' })}
              </span>
            )}
            {(share.isOwner || share.isAdmin) && (
              <button type="button" className={`sp-btn ${panel === 'sharers' ? 'cap-on' : ''}`}
                onClick={() => setPanel(panel === 'sharers' ? '' : 'sharers')}>
                <Lock size={13} /> {t('doc.sharers')}
              </button>
            )}
            <button type="button" className="sp-btn dl-danger" onClick={() => setConfirming(true)}>
              <Trash2 size={13} /> {t('doc.remove')}
            </button>
          </div>
        )}
      </div>
      {!!data.summary && <p className="dl-summary">{data.summary}</p>}
      {/* WHAT CATCHY READS OF IT (task #105). Drawn for everybody when it is on — a reader should know Catchy answers from
          it — and the switch only for the folder's owners and Admins; nothing at all for the rest when it is off. */}
      {(catchy.on || (catchy.may && !!data.currentVersionId)) && (
        <div className={`dl-catchy ${catchy.on ? 'on' : ''}`} data-dl-catchy={catchy.on ? (catchy.on.waiting ? 'waiting' : 'on') : 'off'}>
          <Mascot className="dl-catchy-bird" />
          {catchy.on ? (
            <span className="dl-catchy-text">
              {t('xDoc.catchyLive', { version: catchy.on.label, by: catchy.on.by || '—', when: when(catchy.on.at) })}
              {catchy.on.waiting && <em>{t('xDoc.catchyWaiting', { current: current?.label || '', live: catchy.on.label })}</em>}
              {/* who it is shared with, and who set that (Jay task #1186) */}
              {!!catchy.on.audience && (
                <em className="dl-aud-line" data-dl-audline={catchy.on.audience.mode}>
                  {t('xDoc.audLine', { who: audWho(catchy.on.audience, audNames, t) })}
                  {catchy.on.audience.by ? ` — ${t('xDoc.audSetBy', { by: catchy.on.audience.by, when: when(catchy.on.audience.at) })}` : ''}
                </em>
              )}
            </span>
          ) : <span className="dl-catchy-text dl-dim">{t('xDoc.catchyOffState')}</span>}
          {catchy.may && (
            <span className="dl-catchy-acts">
              {(!catchy.on || catchy.on.waiting) && (
                <button type="button" className="sp-btn primary" disabled={catchyBusy} onClick={() => setCatchyAsk('on')} data-dl-catchy-on>
                  {catchy.on ? t('xDoc.catchyActivateNew', { version: current?.label || '' }) : t('xDoc.catchyActivate')}
                </button>
              )}
              {!!catchy.on && (
                <button type="button" className="sp-btn" disabled={catchyBusy} onClick={() => setCatchyAsk('aud')} data-dl-catchy-aud>
                  <Users size={13} /> {t('xDoc.audEdit')}
                </button>
              )}
              {!!catchy.on && (
                <button type="button" className="sp-btn" disabled={catchyBusy} onClick={() => setCatchyAsk('off')} data-dl-catchy-off>
                  {t('xDoc.catchyDeactivate')}
                </button>
              )}
            </span>
          )}
        </div>
      )}
      {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
      {!!msg && <div className="dl-ok-msg"><Check size={13} /> {msg}</div>}
      {catchyAsk === 'on' && !catchy.on && (
        <AudienceDialog docId={data.docId} activate subject={`${data.code} — ${data.title} · ${current?.label || ''}`}
          onActivate={(aud) => switchCatchy(true, aud)} onCancel={() => setCatchyAsk('')} />
      )}
      {catchyAsk === 'aud' && (
        <AudienceDialog docId={data.docId} subject={`${data.code} — ${data.title}`}
          onSaved={(who) => { setCatchyAsk(''); setMsg(t('xDoc.audSaved', { who })); load(); }} onCancel={() => setCatchyAsk('')} />
      )}
      {catchyAsk === 'on' && !!catchy.on && (
        <Confirm
          title={t('xDoc.catchyWarnTitle')}
          subject={`${data.code} — ${data.title} · ${current?.label || ''}`}
          body={t('xDoc.catchyWarn')}
          confirmLabel={catchy.on ? t('xDoc.catchyActivateNew', { version: current?.label || '' }) : t('xDoc.catchyActivate')}
          cancelLabel={t('doc.cancel')}
          onConfirm={() => switchCatchy(true)}
          onCancel={() => setCatchyAsk('')}
        />
      )}
      {catchyAsk === 'off' && (
        <Confirm
          title={t('xDoc.catchyOffTitle')}
          subject={`${data.code} — ${data.title}`}
          body={t('xDoc.catchyOffBody')}
          danger
          confirmLabel={t('xDoc.catchyDeactivate')}
          cancelLabel={t('doc.cancel')}
          onConfirm={() => switchCatchy(false)}
          onCancel={() => setCatchyAsk('')}
        />
      )}

      {confirming && (
        <Confirm
          title={t('doc.removeTitle')}
          subject={`${data.code} — ${data.title}`}
          body={t('doc.removeConfirm')}
          danger
          confirmLabel={t('doc.remove')}
          cancelLabel={t('doc.cancel')}
          onConfirm={remove}
          onCancel={() => setConfirming(false)}
        />
      )}

      {panel === 'version' && (
        <UploadPanel
          docs={[]}
          presetDoc={{ docId: data.docId, code: data.code, title: data.title, versionLabel: current?.label, authorUserId: current?.authorUserId || data.authorUserId || null }}
          onClose={() => setPanel('')}
          onDone={(c) => {
            setPanel('');
            if (c === 'live') setMsg(t('xDoc.catchyUploadLive'));
            if (c === 'waiting') setMsg(t('xDoc.catchyUploadWaiting'));
            if (c === 'unreadable') setErr(t('xDoc.catchyUploadUnreadable'));
            load();
          }}
        />
      )}

      {panel === 'edit' && (
        <EditDoc
          doc={data}
          canShare={share.canKind}
          canFixVersion={share.canFixVersion}
          onClose={() => setPanel('')}
          onSaved={(m) => { setPanel(''); setMsg(m); load(); }}
          onKindChanged={(newCode, oldCode, n) => {
            setPanel(''); setMsg(t('xDoc.kindChanged', { code: newCode, old: oldCode, n }));
            navigate(`/documents/${encodeURIComponent(newCode)}`, { replace: true });
          }}
        />
      )}

      {panel === 'readers' && <Readers docId={data.docId} onClose={() => setPanel('')} />}
      {panel === 'sharers' && (
        <Sharers
          doc={data}
          isOwner={share.isOwner}
          onClose={() => setPanel('')}
          onSaved={(m) => setMsg(m)}
        />
      )}
      {panel === 'share' && (
        <Share
          doc={data}
          isAdmin={share.isAdmin}
          onClose={() => setPanel('')}
          onSent={(m) => { setPanel(''); setMsg(m); load(); }}
        />
      )}

      {data.currentVersionId
        ? (() => {
          const shown = showing || current;
          /* the CURRENT version's id too, not nothing: with no id the address was the same before and after an
             upload, so the frame kept the old file until the page was reloaded (found 29/10 with the AI change line).
             The version BEING SHOWN decides how to draw it, not the current one: an older version may be an HTML file
             where the newest is a PDF. */
          const vid = showing?.versionId || data.currentVersionId;
          const files = langFiles;
          if (files.length < 2) return <Viewer docId={data.docId} versionId={vid} title={data.title} fileName={shown?.fileName || ''} />;
          const v = langV;
          const langs = [...new Set(files.map((f) => f.lang))];
          const cur = fileOfKey(v.one);
          const remember = (both) => { try { localStorage.setItem('dlLangView', both ? 'both' : ''); } catch (e) { /* ignore */ } };
          const pickOne = (k) => { setLangView({ one: k }); remember(false); };
          const pickBoth = () => {
            const left = v.one || files[0].key;
            setLangView({ both: true, left, right: (files.find((f) => f.key !== left) || files[1]).key });
            remember(true);
          };
          const frame = (f, key) => <Viewer key={key} docId={data.docId} versionId={vid} title={`${data.title} — ${LANG_NAME[f.lang] || ''}`} fileName={f.fileName} part={f.part || undefined} />;
          const sidePick = (which) => (
            <select value={v[which]} onChange={(e) => setLangView({ ...v, [which]: e.target.value })} aria-label={t(which === 'left' ? 'xDoc.leftLang' : 'xDoc.rightLang')} data-dl-side={which}>
              {files.map((f, i) => <option key={f.key} value={f.key}>{fileLabel(f, i)}</option>)}
            </select>
          );
          const parts = v.both ? [] : partsOfLang(cur.lang);
          return (
            <>
              {/* a tab for each language the version HAS (Max 02/11: "tab chỉ hiện ngôn ngữ của file được upload") */}
              <div className="dl-langbar" role="tablist" aria-label={t('xDoc.langBar')} data-dl-langbar>
                <Languages size={15} aria-hidden="true" />
                {langs.length > 1 && langs.map((l, i) => (
                  <button key={l || i} type="button" role="tab" aria-selected={!v.both && cur.lang === l} className={!v.both && cur.lang === l ? 'on' : ''}
                    onClick={() => pickOne(partsOfLang(l)[0].key)} data-dl-lang={l}>{LANG_NAME[l] || t('xDoc.fileN', { n: i + 1 })}</button>
                ))}
                {langs.length === 1 && <span className="dl-langone">{LANG_NAME[langs[0]] || ''}</span>}
                <button type="button" role="tab" aria-selected={!!v.both} className={v.both ? 'on' : ''} onClick={pickBoth} data-dl-lang="both">
                  <Columns2 size={14} aria-hidden="true" /> {t('xDoc.sideBySide')}
                </button>
              </div>
              {/* the parts of the language on screen (Max 03/11: two Vietnamese files in one document) */}
              {parts.length > 1 && (
                <div className="dl-partbar" role="tablist" aria-label={t('xDoc.partBar')} data-dl-partbar>
                  {parts.map((f) => (
                    <button key={f.key} type="button" role="tab" aria-selected={cur.key === f.key} className={cur.key === f.key ? 'on' : ''}
                      onClick={() => pickOne(f.key)} title={f.fileName} data-dl-part={f.partNo || 1}>{noExt(f.fileName) || t('xDoc.partN', { n: f.partNo || 1 })}</button>
                  ))}
                </div>
              )}
              {v.both ? (
                /* two columns, each choosing its file — three or four narrow columns would be too tight to read */
                <div className="dl-twin" data-dl-twin>
                  <div><div className="dl-twin-h">{sidePick('left')}</div>{frame(fileOfKey(v.left), `l-${v.left}`)}</div>
                  <div><div className="dl-twin-h">{sidePick('right')}</div>{frame(fileOfKey(v.right), `r-${v.right}`)}</div>
                </div>
              ) : frame(cur, `o-${cur.key}`)}
            </>
          );
        })()
        : <div className="dl-empty">{t('doc.noVersion')}</div>}

      {!!data.currentVersionId && !showing && (
        <div className="dl-ack">
          {data.acknowledged ? (
            <span className="dl-acked"><Check size={15} /> {t('doc.youAcknowledged', { version: current?.label })}</span>
          ) : (
            <button type="button" className="sp-btn primary dl-ackbtn" onClick={acknowledge} disabled={acking}>
              <Check size={14} /> {acking ? t('doc.recording') : t('doc.iHaveRead')}
            </button>
          )}
        </div>
      )}

      <Comments docId={data.docId} canEdit={canEdit} />

      <div className="dl-history">
        <h3><History size={14} /> {t('doc.history')}</h3>
        <table className="dl-table">
          <thead>
            <tr>
              <th>{t('doc.version')}</th><th>{t('doc.changeNote')}</th>
              <th>{t('doc.author')}</th><th>{t('doc.uploadedBy')}</th><th>{t('doc.updated')}</th><th />
            </tr>
          </thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.versionId} className={v.versionId === data.currentVersionId ? 'on' : ''}>
                <td><strong>{v.label}</strong>{v.versionId === data.currentVersionId && <em> {t('doc.latest')}</em>}
                  {(v.others || []).length > 0 && (
                    <span className="dl-langs" title={[v.lang, ...v.others.map((o) => o.lang)].map((l) => LANG_NAME[l] || '?').join(' · ')} data-dl-hlangs={v.label}>
                      {/* a language with parts reads "VI×2" (03/11) */}
                      {[...new Set([v.lang, ...v.others.map((o) => o.lang)])].map((l) => {
                        const n = [v.lang, ...v.others.map((o) => o.lang)].filter((x) => x === l).length;
                        return `${LANG_SHORT[l] || '?'}${n > 1 ? `×${n}` : ''}`;
                      }).join(' · ')}
                    </span>
                  )}</td>
                <td className="dl-hist-note">{v.changeNote || '—'}{v.changeNote && v.changeNoteAi && <> <AiTag size={13} title={t('xDoc.aiNoteTitle')} testid="dl-note-ai" /></>}</td>
                <td data-dl-hauthor={v.label}>{v.author || '—'}</td>
                <td>{v.uploadedBy}</td>
                <td>{when(v.uploadedAt)}</td>
                <td>
                  <button type="button" className="sp-btn tiny"
                    onClick={() => setShowing(v.versionId === data.currentVersionId ? null : v)}>
                    {t('doc.view')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* `presetDoc` is the document this panel was opened FROM — Jay, 2026-09-21: "I find it difficult to
   upload newer version of document upon the exisiting version. It's not intuitive."

   He was right, and the fault was navigational rather than anything to do with versions: the act
   begins while somebody is READING a document and deciding it is out of date, and the only way in
   was to leave it, go back to the list, press Upload, notice a second tab, and find the document
   again in a dropdown of every document there is — re-identifying the thing they had open. With a
   preset there is no mode to choose and nothing to pick: the panel IS this document's. */
function UploadPanel({ docs, onDone, onClose, presetDoc }) {
  const { t } = useTranslation();
  /* NEW DOCUMENT FIRST — Max, 2026-09-13: "đưa tab tài liệu mới lên trước". Unless the panel was
     opened from a document, where a tab offering to create a different one is a wrong turn. */
  const [mode, setMode] = useState(presetDoc ? 'version' : 'new');
  const [kinds, setKinds] = useState([]);
  // no default kind (Jay task #1183: it was Handbook, and Tina's SOPs went in as HB-…) — the uploader chooses
  const [kind, setKind] = useState('');
  const [docId, setDocId] = useState(presetDoc?.docId || docs[0]?.docId || '');
  const [docFind, setDocFind] = useState('');
  /* Every word has to hit, over the code AND the title — the same rule the library's own search
     box uses, so typing "HB handbook" behaves the same in both places. */
  const shownDocs = useMemo(() => {
    const words = docFind.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return docs;
    return docs.filter((d) => {
      const hay = `${d.code} ${d.title}`.toLowerCase();
      return words.every((w) => hasText(hay, w));
    });
  }, [docs, docFind]);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [everyone, setEveryone] = useState(false);
  const [folderId, setFolderId] = useState(null);
  const [note, setNote] = useState('');
  /* THE FILES OF THIS VERSION, one per language (Hue / Max 02/11) — up to the portal's four. The first is the version's
     own file: its version number is read, the AI compares it, Catchy reads it. Each row: { key, file, lang, how } where
     `how` says where the language came from — 'text' (read from the file), 'name' (its file name), '' (nobody knows yet:
     the person picks), 'reading'. */
  const [rows, setRows] = useState([]);
  const file = rows[0]?.file || null;
  const fileInput = useRef(null);
  const langsOk = rows.length < 2 || rows.every((r) => r.lang);
  // languages that hold more than one file: those files become parts of the document (Max 03/11), said under the list
  const partLangs = [...new Set(rows.filter((r, i) => r.lang && rows.findIndex((x) => x.lang === r.lang) !== i).map((r) => r.lang))];
  // each file's part number within its language — the main file is part 1 of its own
  const partOf = (i) => rows.slice(0, i + 1).filter((x) => x.lang === rows[i].lang).length;
  const readAsUrl = (f) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error(t('xDoc.readFileFailed')));
    r.readAsDataURL(f);
  });
  const addFiles = (list) => {
    const all = [...(list || [])];
    const big = all.filter((f) => f.size > DOC_MAX_MB * 1048576);
    if (big.length) setErr(big.map((f) => t('xDoc.tooBig', { name: f.name, mb: (f.size / 1048576).toFixed(1), max: DOC_MAX_MB })).join(' '));
    const picked = all.filter((f) => f.size <= DOC_MAX_MB * 1048576).slice(0, Math.max(0, DOC_MAX_FILES - rows.length));
    if (!picked.length) return;
    const made = picked.map((f) => ({ key: `${f.name}-${f.size}-${Math.random()}`, file: f, lang: '', how: 'reading' }));
    setRows((cur) => [...cur, ...made].slice(0, DOC_MAX_FILES));
    made.forEach(async (m) => {
      let lang = null;
      let ver = null;
      try {
        const res = await fetch(`${API}/lang-guess`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileName: m.file.name, data: await readAsUrl(m.file) }),
        });
        const d = await res.json().catch(() => ({}));
        lang = res.ok ? d.lang || null : null;
        ver = res.ok ? d.version || null : null;
      } catch (e) { /* the name is still there to go by */ }
      /* THE VERSION ON THE MAIN FILE'S COVER (Jay task #1180) — a PDF, Word file or deck too, not only an HTML title: the box
         is filled for the uploader to confirm, unless they have typed their own */
      if (ver && rowsRef.current[0]?.key === m.key) {
        setVersion((v) => (!v || v === detectedRef.current ? ver : v));
        detectedRef.current = ver; setDetected(ver);
      }
      const byName = guessLang(m.file.name);
      setRows((cur) => cur.map((r) => (r.key !== m.key ? r : {
        ...r,
        // a language another row already holds is fine since 03/11 — the two become parts of that language
        lang: r.lang || lang || byName || '',
        how: lang ? 'text' : (byName ? 'name' : ''),
      })));
    });
  };
  useEffect(() => { readVersion(file); }, [file]); // eslint-disable-line react-hooks/exhaustive-deps
  /* The version the DOCUMENT gives itself, read when the file is picked and shown in a box the
     uploader can correct — Max to Jay, 2026-09-17. Empty means the document does not say, and the
     server then keeps counting from the last one (or starts at v1.0 for a new document), which is
     what the placeholder says will happen. */
  const [version, setVersion] = useState('');
  const [detected, setDetected] = useState('');
  const detectedRef = useRef('');
  detectedRef.current = detected;
  const rowsRef = useRef([]);
  rowsRef.current = rows;
  /* THE AI'S LINE BEFORE THE UPLOAD (Max, 29/10: "AI tạo luôn lúc đó để user đọc trước nếu có điều gì thì họ điều chỉnh
     lại"): picking the file sends it to preview-note, which compares it with the current version and writes the line into
     the box — the person reads it and corrects it, then uploads. A line they typed first is never overwritten; one left
     empty (or an upload pressed before the AI answered) is still written by the server at upload, as before. */
  const [aiNote, setAiNote] = useState('');
  const [aiState, setAiState] = useState('');    // '' | writing | done | none
  const aiSeq = useRef(0);
  const noteRef = useRef('');
  noteRef.current = note;
  useEffect(() => {
    if (mode !== 'version' || !file || !docId) { setAiState(''); return undefined; }
    const seq = ++aiSeq.current;
    let alive = true;
    (async () => {
      setAiState('writing');
      try {
        const data = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(new Error('read'));
          r.readAsDataURL(file);
        });
        const res = await fetch(`${API}/${docId}/versions/preview-note`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fileName: file.name, data }),
        });
        const d = await res.json().catch(() => ({}));
        if (!alive || seq !== aiSeq.current) return;
        const line = res.ok ? String(d.changeNote || '').trim() : '';
        if (!line) { setAiState('none'); return; }
        // never over what the person typed; over an earlier AI line (another file picked) is fine
        setAiNote((prevAi) => {
          if (!noteRef.current.trim() || noteRef.current.trim() === prevAi.trim()) setNote(line);
          return line;
        });
        setAiState('done');
      } catch { if (alive && seq === aiSeq.current) setAiState('none'); }
    })();
    return () => { alive = false; };
  }, [file, docId, mode]);
  /* WHO WROTE IT, which is not who is uploading it — Jay, 2026-09-21: "Most of the time I'm the one
     uploading, but the author is someone else". Chosen from the portal's own accounts rather than
     typed, so one person is one author; empty is allowed and means nobody has said. */
  // a new VERSION starts from the current version's author (Jay 30/10: an author per version) — change it if someone else wrote this one
  const [authorId, setAuthorId] = useState(presetDoc?.authorUserId ? String(presetDoc.authorUserId) : '');
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/people`, { credentials: 'include' });
        const d = await res.json();
        if (alive && res.ok) setPeople(d.people || []);
      } catch (e) { /* the picker simply stays empty; an author is optional */ }
    })();
    return () => { alive = false; };
  }, []);

  /* Only the <title>, never the body: a document's own JavaScript can carry things that look like a
     version (DOC-001 has `VERSION_START` and `v2=…` inside it), and a document states its version
     where it states its name. The server reads the same place from the uploaded bytes, so a payload
     that never went through this screen is labelled by the same rule. */
  const readVersion = async (f) => {
    setDetected(''); setVersion('');
    if (!f) return;
    /* ONLY AN HTML FILE STATES ITS VERSION. A PDF's or a .docx's first 16 KB is compressed binary:
       searching it for a title finds nothing at best, and at worst a byte sequence that reads as
       "v2" — which would fill this box with a number nobody wrote. The server makes the same
       distinction at its own door. */
    if (!/\.html?$/i.test(f.name || '')) return;
    try {
      const head = await f.slice(0, 16384).text();
      const title = /<title[^>]*>([\s\S]{0,300}?)<\/title>/i.exec(head);
      const found = title && String(title[1]).match(/\bv\s?\d{1,3}(?:\.\d{1,3})?\b/gi);
      /* One shape, vX.Y — Max, 2026-09-17: `v5` in a title is shown and stored as `v5.0`, so the
         box says exactly what the library will carry. The server normalises at its own door too;
         this is what the person sees before they press Upload. */
      const raw = found ? found[found.length - 1].replace(/\s/g, '').toLowerCase() : '';
      const label = raw && !raw.includes('.') ? `${raw}.0` : raw;
      setDetected(label); setVersion(label);
    } catch { /* an unreadable file is the upload's problem, not this box's */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/meta/kinds`, { credentials: 'include' });
        const d = await res.json();
        if (res.ok) setKinds(d.kinds || []);
      } catch { /* the code is issued by the server either way; this is only the preview */ }
    })();
  }, []);
  const nextCode = kinds.find((k) => k.key === kind)?.nextCode || '…';

  const send = async () => {
    setErr(''); setBusy(true);
    try {
      let id = docId;
      if (mode === 'new') {
        const res = await fetch(API, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            kind, title, summary, folderId,
            visibility: everyone ? 'everyone' : 'audience',
            authorUserId: Number(authorId) || null,
          }),
        });
        const d = await answerOf(res);
        id = d.docId;
      }
      let catchyState;
      if (file) {
        const data = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result));
          r.onerror = () => reject(new Error(t('xDoc.readFileFailed')));
          r.readAsDataURL(file);
        });
        const res = await fetch(`${API}/${id}/versions`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          // A brand-new document has nothing to have changed — Max, 2026-09-13 — so the note is only
          // ever sent when a version is being added to something that already existed.
          body: JSON.stringify({
            fileName: file.name,
            data,
            // A brand-new document has nothing to have changed — Max, 2026-09-13.
            changeNote: mode === 'new' ? '' : note,
            // the AI's line, read and kept unchanged, is stored with the AI mark
            changeNoteAi: mode !== 'new' && !!aiNote && note.trim() === aiNote.trim(),
            // Blank on purpose when the box is empty: the server then falls back to its own rule
            // rather than being told "no version", which is a different instruction.
            ...(version.trim() ? { versionLabel: version.trim() } : {}),
            // this version's author (sql/20261030o); left empty, the server keeps the author of the version before
            ...(Number(authorId) ? { authorUserId: Number(authorId) } : {}),
            ...(rows[0]?.lang ? { lang: rows[0].lang } : {}),
          }),
        });
        const d = await answerOf(res);
        /* the other files, one request each right after (every body stays under the server's limit): another language, or
           a further part of one — `part` numbers the files of a language, the main file being part 1 of its own */
        for (const [j, r] of rows.slice(1).entries()) {
          // eslint-disable-next-line no-await-in-loop
          const res2 = await fetch(`${API}/${id}/versions/${d.versionId}/files`, {
            method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
            // eslint-disable-next-line no-await-in-loop
            body: JSON.stringify({ fileName: r.file.name, data: await readAsUrl(r.file), lang: r.lang, mainLang: rows[0].lang, part: partOf(j + 1) }),
          });
          // eslint-disable-next-line no-await-in-loop
          const d2 = await res2.json().catch(() => ({}));
          if (!res2.ok) throw new Error(t('xDoc.langFileFailed', { label: d.versionLabel, name: r.file.name, error: d2.error || (res2.status === 413 ? t('xDoc.serverTooBig', { mb: DOC_MAX_MB }) : res2.status) }));
        }
        // what became of Catchy's reading of a document that is switched on: live / waiting / unreadable (task #105)
        catchyState = d.catchy;
      }
      onDone(catchyState);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="dl-panel">
      <div className="dl-panel-head">
        <Upload size={14} />
        <strong>{presetDoc ? t('doc.newVersion') : t('doc.upload')}</strong>
        <button type="button" className="sp-btn tiny" onClick={onClose}><X size={12} /></button>
      </div>
      <div className="dl-panel-body">
        {!presetDoc && (
          <div className="dl-tabs">
            <button type="button" className={mode === 'new' ? 'on' : ''} onClick={() => setMode('new')}>
              {t('doc.newDoc')}
            </button>
            <button type="button" className={mode === 'version' ? 'on' : ''} onClick={() => setMode('version')}
              disabled={!docs.length}>
              {t('doc.newVersion')}
            </button>
          </div>
        )}
        {/* HOW TO UPLOAD, on the form itself (Jay task #1190: Mia asked Catchy on 03/11 and it had no steps) — one quiet line
            under each field; the same steps are the E-Docs guide Catchy reads */}
        {mode === 'new' && !presetDoc && <p className="dl-hint-plain dl-uphelp-top" data-dl-help="newver">{t('xDoc.upHelpNewVer')}</p>}

        {mode === 'version' ? (
          presetDoc ? (
            /* NOT A DROPDOWN OF ONE. The document is named, with the version it is at, so the
               panel says what is about to be added to and to what — but there is nothing to
               choose, because the choice was made by opening it. */
            <p className="dl-presetdoc">
              {t('doc.addingVersionTo')} <b>{presetDoc.code} — {presetDoc.title}</b>
              <span>{t('doc.currentlyAt', { version: presetDoc.versionLabel || '—' })}</span>
            </p>
          ) : (
            /* TYPE TO FIND IT — Max, 2026-09-22: "nếu có nhiều tài liệu thì sẽ khó tìm". A
               <select> over seven documents is a list; over seventy it is a scroll, and the code
               somebody knows ("HB-001") cannot be typed at it. A find box over the same list costs
               nothing while the library is small and is the only thing that works once it is not.

               IN FLOW, not floating: this panel scrolls, and an absolutely positioned list on a
               control inside it gets CLIPPED — the Share panel's people picker lost a whole name
               to exactly that on 2026-09-15. */
            /* `.dl-field` is a ROW — label, then one control. This has two controls stacked, so
               it gets its own wrapper rather than a third item landing in a flex row and sitting
               beside the box it belongs under. */
            <div className="dl-field dl-field-top dl-pickdoc">
              <span>{t('doc.docTitle')}<Req /></span>
              <div className="dl-pickcol">
              <input
                className="dl-docfind"
                type="search"
                value={docFind}
                placeholder={t('doc.findDocPh')}
                onChange={(e) => setDocFind(e.target.value)}
              />
              <div className="dl-doclist">
                {shownDocs.length === 0 && <p className="dl-docnone">{t('doc.findDocNone', { q: docFind })}</p>}
                {shownDocs.map((d) => (
                  <button
                    type="button"
                    key={d.docId}
                    className={`dl-docopt${String(d.docId) === String(docId) ? ' on' : ''}`}
                    aria-pressed={String(d.docId) === String(docId)}
                    onClick={() => setDocId(d.docId)}
                  >
                    <b>{d.code}</b>
                    <span>{d.title}</span>
                    <i>{d.versionLabel || '—'}</i>
                  </button>
                ))}
              </div>
              </div>
            </div>
          )
        ) : (
          <>
            <label className="dl-field">
              <span>{t('doc.kind')}<Req /></span>
              <select value={kind} onChange={(e) => setKind(e.target.value)} className={kind ? '' : 'dl-unset'} data-dl-newkind>
                <option value="" disabled>{t('xDoc.kindChoose')}</option>
                {kinds.map((k) => <option key={k.key} value={k.key} title={t(`xDoc.kindAbout_${k.key}`, { defaultValue: '' })}>{k.label} ({k.key})</option>)}
              </select>
              {/* The code is the Portal's to issue, shown so nobody wonders what it will be. */}
              {kind && <span className="dl-autocode">{nextCode}<em>{t('doc.codeAuto')}</em></span>}
            </label>
            {/* what each kind is for, one line (Jay task #1183) */}
            {kind ? <p className="dl-hint-plain dl-kindabout" data-dl-kindabout>{t(`xDoc.kindAbout_${kind}`, { defaultValue: '' })}</p>
              : <p className="dl-hint-plain" data-dl-help="kind">{t('xDoc.upHelpKind')}</p>}
            <label className="dl-field">
              <span>{t('doc.docTitle')}<Req /></span>
              <input value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} placeholder={t('xDoc.titlePlaceholder')} />
            </label>
            <p className="dl-hint-plain" data-dl-help="title">{t('xDoc.upHelpTitle')}</p>
            <label className="dl-field">
              <span>{t('doc.summary')}</span>
              <input value={summary} maxLength={1000} onChange={(e) => setSummary(e.target.value)} />
            </label>
            <div className="dl-field">
              <span>{t('doc.author')}<Req /></span>
              <AuthorPicker people={people} value={authorId} onChange={setAuthorId} emptyLabel={t('xDoc.authorChoose')} testid="new" />
              <span className="dl-hint">{t('doc.authorHint')}</span>
            </div>
            <label className="dl-check">
              <input type="checkbox" checked={everyone} onChange={(e) => setEveryone(e.target.checked)} />
              <span>{t('doc.everyone')}</span>
            </label>
            {!everyone && <p className="dl-hint">{t('doc.narrowHint')}</p>}
          </>
        )}

        {mode === 'new' && <FolderPicker value={folderId} onChange={setFolderId} required />}
        {mode === 'new' && <p className="dl-hint-plain" data-dl-help="folder">{t('xDoc.upHelpFolder')}</p>}
        {/* the files, one per language (Max 02/11); the note says plainly which four languages there are */}
        <div className="dl-field dl-field-top" data-dl-files>
          <span>{t('doc.file')}<Req /></span>
          <div className="dl-filecol">
            {rows.length > 0 && (
              <ul className="dl-filerows">
                {rows.map((r, i) => (
                  <li key={r.key} data-dl-filerow={i}>
                    <span className="dl-filename" title={r.file.name}>{i === 0 && rows.length > 1 && <em className="dl-mainfile">{t('xDoc.mainFile')}</em>}
                      {r.lang && partLangs.includes(r.lang) && <em className="dl-mainfile dl-partno" data-dl-partno={i}>{t('xDoc.partN', { n: partOf(i) })}</em>}{r.file.name}</span>
                    <select value={r.lang} onChange={(e) => setRows((cur) => cur.map((x) => (x.key === r.key ? { ...x, lang: e.target.value, how: 'picked' } : x)))}
                      aria-label={t('xDoc.langOf', { name: r.file.name })} data-dl-filelang={i} disabled={r.how === 'reading'}>
                      <option value="">{r.how === 'reading' ? t('xDoc.langReading') : t('xDoc.langPick')}</option>
                      {DOC_LANGS.map((l) => <option key={l} value={l}>{LANG_NAME[l]}</option>)}
                    </select>
                    <span className="dl-langhow">{r.how === 'text' ? t('xDoc.howText') : r.how === 'name' ? t('xDoc.howName') : ''}</span>
                    <button type="button" className="sp-btn tiny" onClick={() => setRows((cur) => cur.filter((x) => x.key !== r.key))} aria-label={t('xDoc.removeFile', { name: r.file.name })} data-dl-fileremove={i}><X size={12} /></button>
                  </li>
                ))}
              </ul>
            )}
            {rows.length < DOC_MAX_FILES && (
              <button type="button" className="sp-btn" onClick={() => fileInput.current && fileInput.current.click()} data-dl-addfile>
                <Plus size={13} /> {rows.length ? t('xDoc.addFile') : t('xDoc.pickFiles')}
              </button>
            )}
            <input ref={fileInput} type="file" accept={DOC_ACCEPT} multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-dl-fileinput />
            {/* What the library takes, and what it does with each — Max, 2026-09-23 */}
            <span className="dl-hint">{t('doc.fileKinds')}</span>
            <span className="dl-hint" data-dl-langnote>{t('xDoc.langNote')}</span>
            {partLangs.map((l) => <span key={l} className="dl-hint-plain" data-dl-partsnote={l}>{t('xDoc.partsNote', { n: rows.filter((r) => r.lang === l).length, lang: LANG_NAME[l] })}</span>)}
            {rows.length > 1 && !langsOk && <span className="dl-hint dl-hint-warn">{t('xDoc.langsMissing')}</span>}
          </div>
        </div>
        {/* who wrote THIS version (Jay 30/10) — on a new document the author above is the first version's */}
        {mode === 'version' && (
          <div className="dl-field" data-dl-vauthor>
            <span>{t('doc.versionAuthor')}</span>
            <AuthorPicker people={people} value={authorId} onChange={setAuthorId} emptyLabel={t('doc.authorSame')} testid="version" />
          </div>
        )}
        <label className="dl-field">
          <span>{t('doc.versionLabel')}</span>
          <input value={version} onChange={(e) => setVersion(e.target.value)}
            placeholder={t('doc.versionAuto')} />
          {/* Only once a file has been picked: before that there is no document to say anything
              about, and "this document does not state a version" about nothing reads as a verdict
              on the file the person is holding. */}
          {!!file && (
            <span className="dl-hint">
              {detected ? t('doc.versionFound', { version: detected }) : t('doc.versionNotFound')}
            </span>
          )}
        </label>
        <p className="dl-hint-plain" data-dl-help="version">{mode === 'new' ? t('xDoc.upHelpVersionNew') : t('xDoc.upHelpVersion')}</p>
        {mode === 'version' && (
          <label className="dl-field">
            <span>{t('doc.changeNoteLabel')}</span>
            {/* Jay 28/10 + Max 29/10: picking the file has the AI write this line here, to be read and corrected */}
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('xDoc.aiNotePh')} data-dl-note="1" />
          </label>
        )}
        {mode === 'version' && aiState === 'writing' && <p className="dl-hint-plain dl-ai-state" data-dl-ai="writing">{t('xDoc.aiReading')}</p>}
        {mode === 'version' && aiState === 'done' && note.trim() === aiNote.trim() && (
          <p className="dl-hint-plain dl-ai-state" data-dl-ai="done"><AiTag size={13} title={t('xDoc.aiNoteTitle')} /> {t('xDoc.aiNoteReview')}</p>
        )}

        {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
        <div className="dl-panel-foot">
          {/* what still keeps Upload off on a new document (Jay task #1183: kind and author have no default) */}
          {mode === 'new' && !!file && (!kind || !Number(authorId) || !folderId) && (
            <span className="dl-hint" data-dl-needs>{[!kind ? t('xDoc.kindChoose') : '', !Number(authorId) ? t('xDoc.authorChoose') : '', !folderId ? t('xDoc.folderChoose') : ''].filter(Boolean).join(' · ')}</span>
          )}
          <button type="button" className="sp-btn primary" onClick={send} data-dl-send
            disabled={busy || !file || !langsOk || (mode === 'new' && (!title.trim() || !kind || !Number(authorId) || !folderId))}>
            {busy ? (mode === 'version' && !note.trim() ? t('xDoc.aiWriting') : t('doc.uploading')) : t('doc.upload')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* THE FOLDER RAIL — Jay, 2026-09-14. Where a document is filed, beside the list rather than above
   it: a folder is a place you stand in while you read, not a filter you set and forget.

   Counts add UP the tree. "Vendor Documents 4" when Dev holds 2 and Costing 2 is the number a person
   expects; the server reports only what each folder holds directly, so the adding up happens here,
   once, rather than in two SQL statements that could disagree. */
/* THE OWNERS OF A FOLDER AND OF EVERY FOLDER ABOVE IT (Jay task #105, Max 30/10): they switch its documents on for
   Catchy and, with Admins, they alone change the folder. The server decides (docCatchy.js); this only draws it. */
const ownersUpOf = (folders, folderId) => {
  const byId = new Map(folders.map((f) => [f.folderId, f]));
  const out = []; const seen = new Set();
  let cur = folderId;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const f = byId.get(cur);
    if (!f) break;
    (f.owners || []).forEach((o) => { if (!out.some((x) => x.userId === o.userId)) out.push(o); });
    cur = f.parentId || null;
  }
  return out;
};

/* An Admin picks a folder's owners. Its own overlay layer, like Confirm — the shared modal layer has swallowed clicks. */
function OwnersDialog({ folder, folders, onClose, onSaved }) {
  const { t } = useTranslation();
  const [people, setPeople] = useState(null);
  const [picked, setPicked] = useState(() => new Set((folder.owners || []).map((o) => o.userId)));
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/people`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
        setPeople(d.people || []);
      } catch (e) { setErr(e.message); setPeople([]); }
    })();
  }, []);
  const inherited = ownersUpOf(folders, folder.parentId).filter((o) => !picked.has(o.userId));
  const term = q.trim().toLowerCase();
  const shown = (people || []).filter((p) => !term || `${p.label} ${p.email}`.toLowerCase().includes(term));
  const toggle = (id) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API}/folders/${folder.folderId}/owners`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: [...picked] }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      onSaved(d);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="dl-confirm-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="dl-confirm dl-owners" role="dialog" aria-modal="true" data-dl-owners={folder.folderId}>
        <h4>{t('xDoc.ownersOf', { name: folder.name })}</h4>
        <p>{t('xDoc.ownersHint')}</p>
        {!!inherited.length && <p className="dl-owners-up">{t('xDoc.ownersInherited', { names: inherited.map((o) => o.name).join(', ') })}</p>}
        <span className="dl-search dl-owners-search">
          <Search size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('xDoc.ownersSearch')} />
        </span>
        <div className="dl-owners-list">
          {people === null && <div className="dl-empty">…</div>}
          {shown.map((p) => (
            <label key={p.userId} className={picked.has(p.userId) ? 'on' : ''}>
              <input type="checkbox" checked={picked.has(p.userId)} onChange={() => toggle(p.userId)} data-dl-owner={p.userId} />
              <span>{p.label}</span><em>{p.email}</em>
            </label>
          ))}
        </div>
        {!!err && <div className="sp-error"><AlertTriangle size={13} /> {err}</div>}
        <div className="dl-confirm-foot">
          <button type="button" className="sp-btn" onClick={onClose}>{t('doc.cancel')}</button>
          <button type="button" className="sp-btn primary" onClick={save} disabled={busy || people === null} data-dl-owners-save>
            <Users size={13} /> {t('xDoc.ownersSave')} ({picked.size})
          </button>
        </div>
      </div>
    </div>
  );
}

/* EVERY DOCUMENT CATCHY READS (Jay task #105: "show who activated a document and when, and list all activated documents in
   one view") — a shelf of its own in the folder rail. */
function CatchyList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/catchy`, { credentials: 'include' });
        const d = await res.json();
        if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
        setRows(d.documents || []);
      } catch (e) { setErr(e.message); setRows([]); }
    })();
  }, []);
  return (
    <div data-dl-catchy-list>
      <p className="dl-catchy-hint">{t('xDoc.catchyListHint')}</p>
      {!!err && <div className="sp-error"><AlertTriangle size={14} /> {err}</div>}
      {rows === null && <div className="dl-empty">…</div>}
      {rows && !rows.length && !err && <div className="dl-empty">{t('xDoc.catchyNone')}</div>}
      {!!rows?.length && (
        <table className="dl-list">
          <thead>
            <tr>
              <th className="dl-c-code">{t('doc.code')}</th><th>{t('doc.docTitle')}</th><th>{t('xDoc.colFolder')}</th>
              <th className="dl-c-ver">{t('xDoc.colLive')}</th><th>{t('xDoc.audTitle')}</th><th>{t('xDoc.colBy')}</th><th className="dl-c-when">{t('xDoc.colWhen')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.docId} onClick={() => navigate(`/documents/${d.code}`)} data-dl-catchy-row={d.code}>
                <td className="dl-c-code"><span className="dl-code">{d.code}</span></td>
                <td><strong>{d.title}</strong></td>
                <td className="dl-dim">{d.folderPath || t('doc.unfiled')}</td>
                <td className="dl-c-ver">
                  <span className="dl-ver">{d.liveLabel}</span>
                  {d.waiting && <span className="dl-catchy-wait" title={t('xDoc.catchyWaiting', { current: d.currentLabel, live: d.liveLabel })}>{d.currentLabel} · {t('xDoc.catchyWaitingBadge')}</span>}
                </td>
                {/* who Catchy shares it with (task #1186) — department and role keys, Admins always */}
                <td className="dl-dim" data-dl-catchy-aud={d.audience?.mode}>{audWho(d.audience, null, t)}</td>
                <td>{d.by}</td>
                <td className="dl-c-when">{when(d.at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FolderRail({ folders, docs, picked, onPick, canEdit, onChanged, me }) {
  const { t } = useTranslation();
  const [owning, setOwning] = useState(null);       // the folder whose owners an Admin is picking
  const [adding, setAdding] = useState(null);      // parentId being added to, or 0 for the top
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState(null);
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const call = async (url, opts) => {
    setBusy(true); setErr('');
    try {
      const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      onChanged(d.folders || []);
      return true;
    } catch (e) { setErr(e.message); return false; }
    finally { setBusy(false); }
  };

  const kidsOf = (id) => folders.filter((f) => (f.parentId || null) === (id || null));
  // Documents directly in a folder, plus everything in its sub-folders.
  const deepCount = (id) => docs.filter((d) => d.folderId === id).length
    + kidsOf(id).reduce((sum, k) => sum + deepCount(k.folderId), 0);

  /* a folder with owners (its own or a parent's) is changed by them and Admins only — the server says the same */
  const keeps = (f) => {
    const up = ownersUpOf(folders, f.folderId);
    return !up.length || !!me?.isAdmin || up.some((o) => o.userId === me?.userId);
  };

  const row = (f, depth) => (
    <React.Fragment key={f.folderId}>
      <div className={`dl-fold ${picked === f.folderId ? 'on' : ''}`} style={{ paddingLeft: 12 + depth * 20 }}
        title={f.owners?.length ? t('xDoc.ownersLine', { names: f.owners.map((o) => o.name).join(', ') }) : undefined}>
        {renaming === f.folderId ? (
          <input
            className="dl-fold-edit"
            autoFocus
            defaultValue={f.name}
            onKeyDown={async (e) => {
              if (e.key === 'Escape') setRenaming(null);
              if (e.key === 'Enter' && e.target.value.trim()) {
                if (await call(`${API}/folders/${f.folderId}`, { method: 'PUT', body: JSON.stringify({ name: e.target.value.trim() }) })) setRenaming(null);
              }
            }}
            onBlur={() => setRenaming(null)}
          />
        ) : (
          <>
            <button type="button" className="dl-fold-name" onClick={() => onPick(f.folderId)} data-dl-folder={f.name}>
              <FolderIcon size={15} /> <span>{f.name}</span>
              {!!f.owners?.length && <Lock size={10} className="dl-fold-lock" />}
              <em>{deepCount(f.folderId)}</em>
            </button>
            {((canEdit && keeps(f)) || me?.isAdmin) && (
              <span className="dl-fold-acts">
                {canEdit && keeps(f) && (
                  <>
                    <button type="button" title={t('doc.newFolderIn', { name: f.name })} onClick={() => { setAdding(f.folderId); setName(''); }}>+</button>
                    <button type="button" title={t('doc.renameFolder')} onClick={() => setRenaming(f.folderId)}><Pencil size={11} /></button>
                    <button type="button" title={t('doc.deleteFolder')} onClick={() => setAsking(f)}><X size={11} /></button>
                  </>
                )}
                {me?.isAdmin && (
                  <button type="button" title={t('xDoc.ownersOf', { name: f.name })} onClick={() => setOwning(f)} data-dl-owners-open={f.name}>
                    <Users size={11} />
                  </button>
                )}
              </span>
            )}
          </>
        )}
      </div>
      {adding === f.folderId && (
        <div className="dl-fold-new" style={{ paddingLeft: 28 + depth * 20 }}>
          <input
            autoFocus
            value={name}
            placeholder={t('doc.folderName')}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Escape') setAdding(null);
              if (e.key === 'Enter' && name.trim()) {
                if (await call(`${API}/folders`, { method: 'POST', body: JSON.stringify({ name: name.trim(), parentId: f.folderId }) })) { setAdding(null); setName(''); }
              }
            }}
            onBlur={() => setAdding(null)}
          />
        </div>
      )}
      {kidsOf(f.folderId).map((k) => row(k, depth + 1))}
    </React.Fragment>
  );

  const unfiled = docs.filter((d) => !d.folderId).length;
  return (
    <aside className="dl-rail">
      <div className="dl-rail-head">
        <strong>{t('doc.folders')}</strong>
        {canEdit && (
          <button type="button" className="dl-rail-add" title={t('doc.newFolder')}
            onClick={() => { setAdding(0); setName(''); }} disabled={busy}>
            <Plus size={12} />
          </button>
        )}
      </div>
      {!!err && <p className="dl-rail-err">{err}</p>}
      <div className={`dl-fold ${picked === 'all' ? 'on' : ''}`}>
        <button type="button" className="dl-fold-name" onClick={() => onPick('all')}>
          <span>{t('doc.allDocs')}</span><em>{docs.length}</em>
        </button>
      </div>
      {/* the documents Catchy reads for all staff (task #105) — counted from what this person's list holds; the shelf itself
          lists every one of them */}
      <div className={`dl-fold ${picked === 'catchy' ? 'on' : ''}`}>
        <button type="button" className="dl-fold-name" onClick={() => onPick('catchy')} data-dl-folder="catchy">
          <Mascot className="dl-catchy-bird" /> <span>{t('xDoc.catchyList')}</span><em>{docs.filter((d) => d.catchy).length}</em>
        </button>
      </div>
      {adding === 0 && (
        <div className="dl-fold-new">
          <input
            autoFocus
            value={name}
            placeholder={t('doc.folderName')}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Escape') setAdding(null);
              if (e.key === 'Enter' && name.trim()) {
                if (await call(`${API}/folders`, { method: 'POST', body: JSON.stringify({ name: name.trim() }) })) { setAdding(null); setName(''); }
              }
            }}
            onBlur={() => setAdding(null)}
          />
        </div>
      )}
      {kidsOf(null).map((f) => row(f, 0))}
      {/* Always there, even at zero: it is how somebody notices a document nobody filed. */}
      <div className={`dl-fold ${picked === 'none' ? 'on' : ''}`}>
        <button type="button" className="dl-fold-name" onClick={() => onPick('none')}>
          <span className="dl-dim">{t('doc.unfiled')}</span><em>{unfiled}</em>
        </button>
      </div>

      {!!asking && (
        <Confirm
          title={t('doc.deleteFolder')}
          body={t('doc.deleteFolderAsk', { name: asking.name })}
          danger
          confirmLabel={t('doc.deleteFolder')}
          cancelLabel={t('doc.cancel')}
          onCancel={() => setAsking(null)}
          onConfirm={async () => {
            if (await call(`${API}/folders/${asking.folderId}`, { method: 'DELETE' })) setAsking(null);
            else setAsking(null);
          }}
        />
      )}
      {!!owning && (
        <OwnersDialog
          folder={owning}
          folders={folders}
          onClose={() => setOwning(null)}
          onSaved={(d) => { onChanged(d.folders || []); setOwning(null); }}
        />
      )}
    </aside>
  );
}

/* THE GUIDE (Max 01/11: every module has one, ending on the manual). It belongs to the list view only — a single
   document (/documents/:code) has no ModuleHelp. Upload is drawn only for someone who may edit, so that step is skipped
   for the rest. */
const TOUR = [
  { selector: '.dl-title-right .sp-btn.primary', title: 'tourDocs.uploadT', body: 'tourDocs.uploadB' },
  { selector: '.dl-search', title: 'tourDocs.searchT', body: 'tourDocs.searchB' },
  { selector: '[data-dl-kind-filter]', title: 'tourDocs.kindT', body: 'tourDocs.kindB' },
  { selector: '.dl-rail', title: 'tourDocs.foldersT', body: 'tourDocs.foldersB' },
  { selector: '.dl-list', title: 'tourDocs.listT', body: 'tourDocs.listB' },
];

export default function DocumentLibrary() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { permissions } = useAuth();
  const mayEdit = (permissions || {}).document_library === 'edit';
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [msg, setMsg] = useFlash('');
  /* Which shelf is being stood in. 'all' and 'none' are not folder ids — they are the two answers a
     folder id cannot give (everything, and everything nobody filed). */
  const [folders, setFolders] = useState([]);
  const [picked, setPicked] = useState('all');
  // who is looking, as the folder routes say it (task #105) — which folders they keep, whether they pick owners
  const [folderMe, setFolderMe] = useState(null);
  // the Kind filter (Jay task #1117) and the documents ticked for Catchy (task #1120)
  const [kinds, setKinds] = useState([]);
  const [kindF, setKindF] = useState('');
  const [sel, setSel] = useState(() => new Set());
  const [bulk, setBulk] = useState(null);           // the server's dry answer: { on, done, skipped }
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [res, fres] = await Promise.all([
        fetch(API, { credentials: 'include' }),
        fetch(`${API}/folders`, { credentials: 'include' }),
      ]);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || i18n.t('xDoc.failed', { status: res.status }));
      setDocs(d.documents || []); setKinds(d.kinds || []); setErr('');
      // The folders are an extra: a library that cannot draw its shelves still lists its documents.
      if (fres.ok) { const fd = await fres.json(); setFolders(fd.folders || []); setFolderMe(fd.me || null); }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  /* Reloaded on every arrival at the list, not once per mount: /documents and /documents/HB-001 are
     the same component, so coming back from a document is a re-render, and a list loaded once shows
     what was true before the document was removed or a new version was published. */
  useEffect(() => { if (!code) load(); }, [code, load]);

  /* Search over what is ON THE ROW — code, title, summary and the change note. Hundreds of documents
     are found by half-remembering one of those, not by filtering a field somebody has to choose. */
  /* A folder and its sub-folders, because standing in "Vendor Documents" and being shown nothing
     while three sub-folders hold four documents is not what the word means. */
  const inFolder = useCallback((docFolderId) => {
    if (picked === 'all') return true;
    if (picked === 'none') return !docFolderId;
    if (!docFolderId) return false;
    /* Walk UP from the document's own folder to the root and see whether the picked one is on the
       way. Going up visits one folder per level; going down would have to visit every branch. The
       `seen` set is not defensive dressing — a folder whose parent was moved under its own child
       would otherwise spin here for ever, and the table allows that shape. */
    // Indexed once rather than a .find() per level: the closure inside a loop is what the linter
    // objects to, and a map is the better answer anyway once a library has many folders.
    const parentOf = new Map(folders.map((f) => [f.folderId, f.parentId || null]));
    let cur = docFolderId;
    const seen = new Set();
    while (cur && !seen.has(cur)) {
      if (cur === picked) return true;
      seen.add(cur);
      cur = parentOf.get(cur) || null;
    }
    return false;
  }, [picked, folders]);

  const searched = useMemo(() => {
    const term = q.trim().toLowerCase();
    const inside = docs.filter((d) => inFolder(d.folderId) && (!kindF || kindOf(d.code) === kindF));
    if (!term) return inside;
    return inside.filter((d) => [d.code, d.title, d.summary, d.changeNote]
      .some((v) => hasText(String(v || '').toLowerCase(), term)));
  }, [docs, q, inFolder, kindF]);
  const kindCount = useMemo(() => docs.reduce((m, d) => { const k = kindOf(d.code); m[k] = (m[k] || 0) + 1; return m; }, {}), [docs]);

  /* who may tick a row for Catchy: an Admin, or an owner of the document's folder or of one above it — the server
     checks again, this only keeps a box off a row the press would skip */
  const mayKeep = useCallback((d) => {
    if (!folderMe) return false;
    if (folderMe.isAdmin) return true;
    const byId = new Map(folders.map((f) => [f.folderId, f]));
    let cur = d.folderId; const seen = new Set();
    while (cur && !seen.has(cur)) {
      const f = byId.get(cur); if (!f) break;
      if ((f.owners || []).some((o) => o.userId === folderMe.userId)) return true;
      seen.add(cur); cur = f.parentId || null;
    }
    return false;
  }, [folderMe, folders]);
  const keepsAny = !!folderMe && (folderMe.isAdmin || folders.some((f) => (f.owners || []).some((o) => o.userId === folderMe.userId)));
  const toggle = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const askBulk = async (on) => {
    setErr(''); setBulkBusy(true);
    try {
      const res = await fetch(`${API}/catchy/bulk`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ docIds: [...sel], on, dry: true }) });
      const d = await res.json(); if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      setBulk(d);
    } catch (e) { setErr(e.message); } finally { setBulkBusy(false); }
  };
  const runBulk = async () => {
    setBulkBusy(true);
    try {
      const res = await fetch(`${API}/catchy/bulk`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ docIds: bulk.done.map((x) => x.docId), on: bulk.on }) });
      const d = await res.json(); if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`);
      const skipped = [...bulk.skipped, ...d.skipped];
      setMsg(`${t(d.on ? 'xDoc.bulkDoneOn' : 'xDoc.bulkDoneOff', { n: d.done.length })}${skipped.length ? ` ${t('xDoc.bulkSkipped', { n: skipped.length, list: skipped.map((x) => `${x.code} (${skipWhy(t, x.reason)})`).join(', ') })}` : ''}`);
      setBulk(null); setSel(new Set()); load();
    } catch (e) { setErr(e.message); setBulk(null); } finally { setBulkBusy(false); }
  };

  /* What each column IS, for sorting and for the funnel's value list. `updated` sorts on the raw
     timestamp and lists the printed date: sorting on the printed string would put 1 Oct before
     19 Sept, and a funnel listing ISO timestamps would offer 7 values nobody recognises. */
  const DL_GETTERS = {
    code: (d) => d.code || '',
    title: (d) => d.title || '',
    author: (d) => d.author || '',
    versionLabel: (d) => d.versionLabel || '',
    changeNote: (d) => d.changeNote || '',
    updated: (d) => when(d.uploadedAt),
    status: (d) => (d.unread ? t('doc.unread') : (d.acknowledged ? t('doc.acknowledged') : '')),
  };
  const DL_SORTERS = { ...DL_GETTERS, updated: (d) => d.uploadedAt || '' };
  const shown = useMemo(() => {
    const afterCols = Object.keys(colFilters).length
      ? searched.filter((d) => rowPassesColumnFilters(d, colFilters, DL_GETTERS)) : searched;
    return sort.key ? sortRows(afterCols, sort, DL_SORTERS) : afterCols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, colFilters, sort]);

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(searched, key, colFilters, DL_GETTERS) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((prev) => (prev && prev.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        // Everything ticked is the same as no filter — keeping it would light the funnel for ever.
        const isAll = allValues && allValues.length && values.length >= allValues.length;
        if (!values.length || isAll) delete next[k]; else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });
  const unreadCount = docs.filter((d) => d.unread).length;

  return (
    <div className="sp-page dl-page">
      <Header />
      <div className="sp-main">
        {code ? <DocumentPage code={code} onRemoved={setMsg} /> : (
          <>
            {/* ONE LINE FOR THE TITLE, ONE ROW FOR EVERYTHING ELSE (Max 02/11: "xử lý chỗ khung đổi cho gọn lại") — the
                search, the kind and the count on the left, the buttons on the right of the same row */}
            <div className="dl-title">
              <h2><FileText size={18} /> {t('doc.title')}</h2>
              <p>{t('doc.subtitle')}</p>
            </div>

            <div className="dl-bar">
              <span className="dl-search">
                <Search size={14} />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('doc.search')} />
                {!!q && <button type="button" onClick={() => setQ('')}><X size={12} /></button>}
              </span>
              {picked !== 'catchy' && (
                <select className="dl-kind-filter" value={kindF} onChange={(e) => setKindF(e.target.value)} data-dl-kind-filter>
                  <option value="">{t('xDoc.kindAll')}</option>
                  {kinds.filter((k) => kindCount[k.key] || k.key === kindF).map((k) => <option key={k.key} value={k.key}>{k.label} ({k.key}) · {kindCount[k.key] || 0}</option>)}
                </select>
              )}
              {/* the Catchy shelf lists its own rows — the list's count would read "0 / 11" over it */}
              {picked !== 'catchy' && (
              /* no total (Max 02/11: "số 23 thì bỏ đi") — the folder rail counts already; the unread count stays */
              unreadCount > 0 && <span className="dl-count"><em>{unreadCount} {t('doc.unread')}</em></span>
              )}
              <div className="dl-title-right">
                {mayEdit && (
                  <button type="button" className="sp-btn primary" onClick={() => setUploading((v) => !v)}>
                    <Upload size={13} /> {t('doc.upload')}
                  </button>
                )}
                <button type="button" className="sp-btn" onClick={load} disabled={busy}>
                  <RefreshCw size={13} /> {t('doc.refresh')}
                </button>
                <ModuleHelp manual="documents" steps={TOUR} tourKey="documentLibrary" ready={!busy} />
              </div>
            </div>

            {uploading && (
              <UploadPanel docs={docs} onClose={() => setUploading(false)}
                onDone={() => { setUploading(false); load(); }} />
            )}

            {!!err && <div className="sp-error"><AlertTriangle size={14} /> {err}</div>}
            {!!msg && <div className="dl-ok-msg"><Check size={13} /> {msg}</div>}

            <div className="dl-split">
            <FolderRail
              folders={folders}
              docs={docs}
              picked={picked}
              onPick={setPicked}
              canEdit={mayEdit}
              onChanged={setFolders}
              me={folderMe}
            />
            <div className="dl-listwrap">
            {picked === 'catchy' && <CatchyList />}
            {picked !== 'catchy' && !busy && !docs.length && <div className="dl-empty">{t('doc.none')}</div>}
            {picked !== 'catchy' && !busy && !!docs.length && !shown.length && <div className="dl-empty">{t('doc.noMatch')}</div>}

            {picked !== 'catchy' && sel.size > 0 && (
              <div className="dl-bulkbar" data-dl-bulkbar>
                <span>{t('xDoc.bulkSelected', { n: sel.size })}</span>
                <button type="button" className="sp-btn primary" disabled={bulkBusy} onClick={() => askBulk(true)} data-dl-bulk-on>{t('xDoc.bulkOn')}</button>
                <button type="button" className="sp-btn" disabled={bulkBusy} onClick={() => askBulk(false)} data-dl-bulk-off>{t('xDoc.bulkOff')}</button>
                <button type="button" className="sp-btn" onClick={() => setSel(new Set())} data-dl-bulk-clear>{t('xDoc.bulkClear')}</button>
              </div>
            )}
            {/* the list scrolls in its own box so its header stays put (Max 02/11: "header của bảng cần freeze/stick lại") */}
            {picked !== 'catchy' && !!shown.length && (
              <div className="dl-list-scroll" data-dl-listscroll>
              <table className="dl-list">
                <thead>
                  <tr>
                    {keepsAny && (() => {
                      const mine = shown.filter(mayKeep);
                      const all = mine.length > 0 && mine.every((d) => sel.has(d.docId));
                      return (
                        <th className="dl-c-pick">
                          <input type="checkbox" checked={all} disabled={!mine.length} title={t('xDoc.bulkAll')} aria-label={t('xDoc.bulkAll')} data-dl-pick-all
                            onChange={() => setSel((p) => { const n = new Set(p); mine.forEach((d) => (all ? n.delete(d.docId) : n.add(d.docId))); return n; })} />
                        </th>
                      );
                    })()}
                    <SortTh label={t('doc.code')} className="dl-c-code" {...thProps('code')} />
                    <SortTh label={t('doc.docTitle')} className="dl-c-title" {...thProps('title')} />
                    <SortTh label={t('doc.author')} className="dl-c-author" {...thProps('author')} />
                    <SortTh label={t('doc.version')} className="dl-c-ver" {...thProps('versionLabel')} />
                    <SortTh label={t('doc.changeNote')} className="dl-c-note" {...thProps('changeNote')} />
                    <SortTh label={t('doc.updated')} className="dl-c-when" {...thProps('updated')} />
                    <SortTh label={t('doc.status')} className="dl-c-state" {...thProps('status')} />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((d) => (
                    <tr
                      key={d.docId}
                      className={d.unread ? 'dl-unread' : ''}
                      onClick={() => navigate(`/documents/${d.code}`)}
                    >
                      {keepsAny && (
                        <td className="dl-c-pick" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={sel.has(d.docId)} disabled={!mayKeep(d)} data-dl-pick={d.code}
                            title={mayKeep(d) ? undefined : t('xDoc.bulkNotYours')} onChange={() => toggle(d.docId)} />
                        </td>
                      )}
                      <td className="dl-c-code"><span className="dl-code">{d.code}</span></td>
                      <td>
                        <strong>{d.title}</strong>
                        {!!d.summary && <span className="dl-sum">{d.summary}</span>}
                      </td>
                      {/* A dash, not the uploader: nobody has said who wrote it, and filling it in
                          with whoever pressed Upload is the very thing this column was added to
                          stop. */}
                      <td className="dl-c-author">{d.author || '—'}</td>
                      <td className="dl-c-ver"><span className="dl-ver">{d.versionLabel || '—'}</span></td>
                      <td className="dl-c-note" title={d.changeNote}>{d.changeNote || '—'}{d.changeNote && d.changeNoteAi && <> <AiTag size={12} title={t('xDoc.aiNoteTitle')} /></>}</td>
                      <td className="dl-c-when">{when(d.uploadedAt)}</td>
                      <td className="dl-c-state">
                        {d.unread && <span className="dl-badge">{t('doc.unread')}</span>}
                        {d.acknowledged && <span className="dl-ok"><Check size={12} /> {t('doc.acknowledged')}</span>}
                        {d.catchy && (
                          <span className={`dl-catchy-badge ${d.catchyWaiting ? 'wait' : ''}`} data-dl-catchy-badge={d.code}
                            title={`${d.catchyWaiting ? t('xDoc.catchyWaitingBadge') : t('xDoc.catchyBadgeTitle')} — ${t('xDoc.catchyOnBy', { by: d.catchyBy || '—', when: when(d.catchyAt) })}`}>
                            {t('xDoc.catchyBadge')}{d.catchyWaiting ? ' ·' : ''}
                          </span>
                        )}
                        {/* who switched it on and when, on the row itself (Jay task #1189) */}
                        {d.catchy && <span className="dl-catchy-who" data-dl-catchy-who={d.code}>{t('xDoc.catchyOnBy', { by: d.catchyBy || '—', when: when(d.catchyAt) })}</span>}
                        {/* The conversation is on the row, or nobody outside it ever finds out it exists. */}
                        {d.comments > 0 && (
                          <span className="dl-ccount" title={t('doc.comments')}>
                            <MessageSquare size={11} /> {d.comments}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
            </div>
            </div>
            {bulk && <BulkCatchy plan={bulk} busy={bulkBusy} onConfirm={runBulk} onCancel={() => setBulk(null)} />}
          </>
        )}
      </div>
    </div>
  );
}
